import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  History,
  Minus,
  RefreshCcw,
  RotateCcw,
  Save,
  Search,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API, copy, showError, showSuccess } from '../helpers';
import { Table, Thead, Tbody, Tr, Th, Td } from './ui/Table';
import Button from './ui/Button';
import Card from './ui/Card';
import Badge from './ui/Badge';
import Input from './ui/Input';
import Modal from './ui/Modal';
import AppErrorState from './AppErrorState';
import Loading from './Loading';
import { normalizeAppError } from '../helpers/appRuntime';

const ROUTE_OVERVIEW_TIMEOUT_MS = 30000;
const ROUTE_VARIANT_TIMEOUT_MS = 8000;
const ROUTE_OVERVIEW_RETRY_DELAY_MS = 600;

const selectStyle = {
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-md)',
  padding: '0.5rem 0.625rem',
  fontSize: '0.875rem',
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  minWidth: '140px',
};

const numberInputStyle = {
  width: '76px',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-md)',
  padding: '0.325rem 0.45rem',
  fontSize: '0.875rem',
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  textAlign: 'center',
};

const textareaStyle = {
  width: '100%',
  minHeight: '84px',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-md)',
  padding: '0.55rem 0.625rem',
  fontSize: '0.875rem',
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  lineHeight: 1.5,
  resize: 'vertical',
};

const cellTopStyle = {
  verticalAlign: 'top',
  lineHeight: 1.35,
};

const cellMiddleStyle = {
  verticalAlign: 'middle',
  lineHeight: 1.35,
};

const statusSelectStyle = {
  ...selectStyle,
  minWidth: '96px',
  width: '100%',
};

const helperTextStyle = {
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.35,
};

const stickyHeaderCellStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 4,
  backgroundColor: 'var(--gray-50)',
  boxShadow: 'inset 0 -1px 0 var(--border-color)',
};

const spinButtonStyle = {
  border: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
  color: 'var(--text-secondary)',
  borderRadius: '0.25rem',
  width: '1.15rem',
  height: '1.05rem',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  fontSize: '0.66rem',
  cursor: 'pointer',
};

const formatPrice = (value, digits = 4) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `$${amount.toFixed(digits)}`;
};

const formatCNYPrice = (value, digits = 4) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `¥${amount.toFixed(digits)}`;
};

const formatPercent = (value) => {
  const percent = Number(value);
  if (!Number.isFinite(percent)) return '-';
  return `${percent.toFixed(1)}%`;
};

const formatSignedPercent = (value) => {
  const percent = Number(value);
  if (!Number.isFinite(percent)) return '-';
  return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
};

const formatSignedCNYPrice = (value, digits = 4) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `${amount > 0 ? '+' : ''}¥${Math.abs(amount).toFixed(digits)}`;
};

const formatSignedUSDPrice = (value, digits = 4) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `${amount > 0 ? '+' : ''}$${Math.abs(amount).toFixed(digits)}`;
};

const sleep = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const compareNullableNumber = (a, b) => {
  const aNum = Number(a);
  const bNum = Number(b);
  const aValid = Number.isFinite(aNum);
  const bValid = Number.isFinite(bNum);
  if (!aValid && !bValid) return 0;
  if (!aValid) return 1;
  if (!bValid) return -1;
  return aNum - bNum;
};

const parseInteger = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return parsed;
};

const parsePositiveNumber = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return amount;
};

const formatDraftPriceValue = (value) => {
  if (typeof value === 'string') {
    return value;
  }
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return String(amount);
};

const parseDraftPriceForPayload = (value, label) => {
  const text = String(value ?? '').trim();
  if (!text) return 0;
  const amount = Number(text);
  if (!Number.isFinite(amount) || amount < 0) {
    throw new Error(`${label}格式无效`);
  }
  return amount;
};

const convertUSDToCNY = (priceUSD, rate) => {
  const usd = Number(priceUSD);
  const cnyRate = Number(rate);
  if (
    !Number.isFinite(usd) ||
    !Number.isFinite(cnyRate) ||
    usd < 0 ||
    cnyRate <= 0
  ) {
    return null;
  }
  return usd * cnyRate;
};

const normalizePriceForDisplayCompare = (value) => {
  const amount = parsePositiveNumber(value);
  if (amount <= 0) return 0;
  return Number(amount.toFixed(4));
};

const isPriceBelowRecommended = (manualValue, recommendedValue) => {
  const manual = normalizePriceForDisplayCompare(manualValue);
  const recommended = normalizePriceForDisplayCompare(recommendedValue);
  if (manual <= 0 || recommended <= 0) {
    return false;
  }
  return recommended > manual;
};

const buildPriceComparisonItem = (
  label,
  currentValue,
  recommendedValue,
  unit = '/1M'
) => {
  const current = normalizePriceForDisplayCompare(currentValue);
  const recommended = normalizePriceForDisplayCompare(recommendedValue);
  if (current <= 0 && recommended <= 0) {
    return null;
  }
  if (recommended <= 0) {
    return {
      key: label,
      label,
      unit,
      status: 'unavailable',
      current,
      recommended,
      delta: 0,
      percent: null,
    };
  }
  if (current <= 0) {
    return {
      key: label,
      label,
      unit,
      status: 'missing',
      current,
      recommended,
      delta: recommended,
      percent: 100,
    };
  }
  const delta = Number((current - recommended).toFixed(4));
  const percent = recommended > 0 ? (delta / recommended) * 100 : null;
  if (Math.abs(delta) <= 0.0001) {
    return {
      key: label,
      label,
      unit,
      status: 'equal',
      current,
      recommended,
      delta: 0,
      percent: 0,
    };
  }
  return {
    key: label,
    label,
    unit,
    status: delta > 0 ? 'above' : 'below',
    current,
    recommended,
    delta,
    percent,
  };
};

const getComparisonBadgeColor = (status) => {
  switch (status) {
    case 'above':
      return 'green';
    case 'below':
      return 'red';
    case 'missing':
      return 'yellow';
    case 'equal':
      return 'gray';
    default:
      return 'blue';
  }
};

const getComparisonSummaryText = (comparison) => {
  if (!comparison) return '';
  if (comparison.status === 'equal') {
    return `${comparison.label} 持平`;
  }
  if (comparison.status === 'missing') {
    return `${comparison.label} 未设置`;
  }
  if (comparison.status === 'unavailable') {
    return `${comparison.label} 暂无推荐价`;
  }
  const prefix = comparison.status === 'above' ? '高于建议' : '低于建议';
  return `${comparison.label} ${prefix} ${formatSignedCNYPrice(
    comparison.delta
  )} ${formatSignedPercent(comparison.percent)}`;
};

const getPriceComparisonOverallState = (comparisons) => {
  const actionable = comparisons.filter(
    (item) => item && item.status !== 'equal' && item.status !== 'unavailable'
  );
  if (actionable.length === 0) {
    return comparisons.length > 0 ? 'equal' : 'unknown';
  }
  const hasAbove = actionable.some((item) => item.status === 'above');
  const hasBelow = actionable.some((item) => item.status === 'below');
  const hasMissing = actionable.some((item) => item.status === 'missing');
  if (hasAbove && hasBelow) return 'mixed';
  if (hasBelow) return 'below';
  if (hasAbove) return 'above';
  if (hasMissing) return 'missing';
  return 'equal';
};

const getPriceComparisonSummaryLabel = (state) => {
  switch (state) {
    case 'above':
      return '当前模型售价高于建议价';
    case 'below':
      return '当前模型售价低于建议价';
    case 'mixed':
      return '当前模型售价与建议价存在高低混合';
    case 'missing':
      return '当前模型售价未完整设置';
    case 'equal':
      return '当前模型售价与建议价持平';
    default:
      return '当前模型售价状态待确认';
  }
};

const getPriceComparisonSummaryColor = (state) => {
  switch (state) {
    case 'above':
      return 'var(--success)';
    case 'below':
      return 'var(--error)';
    case 'mixed':
      return 'var(--warning)';
    case 'missing':
      return 'var(--warning)';
    default:
      return 'var(--text-secondary)';
  }
};

const getHistoryPointTotalAggregate = (point) => {
  if (!point) return 0;
  const perCall = parsePositiveNumber(point.aggregate_per_call_cost_cny);
  if (perCall > 0) {
    return perCall;
  }
  return (
    parsePositiveNumber(point.aggregate_prompt_cost_cny_per_1m) +
    parsePositiveNumber(point.aggregate_cache_read_cost_cny_per_1m) +
    parsePositiveNumber(point.aggregate_cache_create_cost_cny_per_1m) +
    parsePositiveNumber(point.aggregate_completion_cost_cny_per_1m)
  );
};

const getHistoryPointTotalRecommended = (point) => {
  if (!point) return 0;
  const perCall = parsePositiveNumber(point.recommended_per_call_price_cny);
  if (perCall > 0) {
    return perCall;
  }
  return (
    parsePositiveNumber(point.recommended_prompt_price_cny_per_1m) +
    parsePositiveNumber(point.recommended_cache_read_price_cny_per_1m) +
    parsePositiveNumber(point.recommended_cache_create_price_cny_per_1m) +
    parsePositiveNumber(point.recommended_completion_price_cny_per_1m)
  );
};

const buildHistoryTrend = (points, selector) => {
  if (!Array.isArray(points) || points.length < 2) {
    return null;
  }
  const latest = selector(points[points.length - 1]);
  const previous = selector(points[points.length - 2]);
  if (!Number.isFinite(latest) || !Number.isFinite(previous)) {
    return null;
  }
  const delta = Number((latest - previous).toFixed(4));
  const percent = previous > 0 ? (delta / previous) * 100 : null;
  let direction = 'flat';
  if (delta > 0.0001) direction = 'up';
  if (delta < -0.0001) direction = 'down';
  return {
    current: latest,
    previous,
    delta,
    percent,
    direction,
  };
};

const formatHistoryTimestamp = (timestamp) => {
  const value = Number(timestamp);
  if (!Number.isFinite(value) || value <= 0) return '-';
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value * 1000));
};

const getPromptPriceUSDForDisplay = (route) => {
  const effective = Number(route?.effective_prompt_price_per_1m);
  if (Number.isFinite(effective) && effective > 0) {
    return effective;
  }
  const prompt = Number(route?.prompt_price_per_1m);
  if (Number.isFinite(prompt) && prompt > 0) {
    return prompt;
  }
  return NaN;
};

const getRouteRowKey = (route) => {
  if (!route) return '';
  const rowKey = String(route.row_key || '').trim();
  if (rowKey) return rowKey;
  const baseRouteId = Number(route.base_route_id || route.id || 0);
  const channelVariantId = Number(route.channel_variant_id || 0);
  if (channelVariantId > 0 && baseRouteId > 0) {
    return `variant:${channelVariantId}:${baseRouteId}`;
  }
  return `route:${baseRouteId}`;
};

const getBaseRouteId = (route) =>
  Number(route?.base_route_id || route?.id || 0);

const normalizeModelName = (value) => {
  const text = String(value || '')
    .trim()
    .toLowerCase();
  if (!text) return '';
  let normalized = text.replace(/^bigmodel\//, '');
  const slashIndex = normalized.lastIndexOf('/');
  if (slashIndex >= 0 && slashIndex < normalized.length - 1) {
    normalized = normalized.slice(slashIndex + 1);
  }
  for (let i = 0; i < 6; i += 1) {
    const next = normalized.replace(
      /^(?:\[[^\]]+\]|【[^】]+】|\([^)]+\)|（[^）]+）|\{[^}]+\}|<[^>]+>)\s*/u,
      ''
    );
    if (next === normalized) break;
    normalized = next.trim();
    if (!normalized) break;
  }
  const colonIndex = normalized.indexOf(':');
  if (colonIndex >= 0) {
    normalized = normalized.slice(0, colonIndex);
  }
  normalized = normalized
    .replace(/[-_](20\d{6}|\d{8})$/i, '')
    .replace(/-20\d{2}-\d{2}-\d{2}$/i, '')
    .replace(/-latest$/i, '');
  return normalized.trim();
};

const createDraftFromRoute = (route) => ({
  priority: Number(route?.priority || 0),
  weight: Number(route?.weight || 0),
  enabled: route?.enabled === true,
  affects_provider_cooldown: route?.affects_provider_cooldown !== false,
});

const routeDraftUnchanged = (original, draft) =>
  Number(draft?.priority ?? 0) === Number(original?.priority ?? 0) &&
  Number(draft?.weight ?? 0) === Number(original?.weight ?? 0) &&
  Boolean(draft?.enabled) === Boolean(original?.enabled) &&
  Boolean(draft?.affects_provider_cooldown) ===
    Boolean(original?.affects_provider_cooldown);

const createModelSaleDraftFromEntry = (entry) => ({
  prompt_price_cny_per_1m: formatDraftPriceValue(
    entry?.manualPromptPriceCNYPer1M
  ),
  cache_read_price_cny_per_1m: formatDraftPriceValue(
    entry?.manualCacheReadPriceCNYPer1M
  ),
  cache_create_price_cny_per_1m: formatDraftPriceValue(
    entry?.manualCacheCreatePriceCNYPer1M
  ),
  completion_price_cny_per_1m: formatDraftPriceValue(
    entry?.manualCompletionPriceCNYPer1M
  ),
  per_call_price_cny: formatDraftPriceValue(entry?.manualPerCallPriceCNY),
  remark: String(entry?.modelRemark || ''),
});

const draftPriceText = (value) => String(value ?? '').trim();
const draftRemarkText = (value) => String(value ?? '').trim();

const modelSaleDraftUnchanged = (entry, draft) =>
  draftPriceText(draft?.prompt_price_cny_per_1m) ===
    draftPriceText(
      createModelSaleDraftFromEntry(entry).prompt_price_cny_per_1m
    ) &&
  draftPriceText(draft?.cache_read_price_cny_per_1m) ===
    draftPriceText(
      createModelSaleDraftFromEntry(entry).cache_read_price_cny_per_1m
    ) &&
  draftPriceText(draft?.cache_create_price_cny_per_1m) ===
    draftPriceText(
      createModelSaleDraftFromEntry(entry).cache_create_price_cny_per_1m
    ) &&
  draftPriceText(draft?.completion_price_cny_per_1m) ===
    draftPriceText(
      createModelSaleDraftFromEntry(entry).completion_price_cny_per_1m
    ) &&
  draftPriceText(draft?.per_call_price_cny) ===
    draftPriceText(createModelSaleDraftFromEntry(entry).per_call_price_cny) &&
  draftRemarkText(draft?.remark) ===
    draftRemarkText(createModelSaleDraftFromEntry(entry).remark);

const routeHasPricingAlert = (route) => {
  if (!route) {
    return false;
  }
  return (
    isPriceBelowRecommended(
      route.manual_prompt_price_cny_per_1m,
      route.model_recommended_prompt_price_cny_per_1m
    ) ||
    isPriceBelowRecommended(
      route.manual_cache_read_price_cny_per_1m,
      route.model_recommended_cache_read_price_cny_per_1m
    ) ||
    isPriceBelowRecommended(
      route.manual_cache_create_price_cny_per_1m,
      route.model_recommended_cache_create_price_cny_per_1m
    ) ||
    isPriceBelowRecommended(
      route.manual_completion_price_cny_per_1m,
      route.model_recommended_completion_price_cny_per_1m
    ) ||
    isPriceBelowRecommended(
      route.manual_per_call_price_cny,
      route.model_recommended_per_call_price_cny
    )
  );
};

const ROUTE_PRICING_ALERT_COUNT_EVENT = 'route-pricing-alert-refresh';
const ROUTE_PRICING_ALERT_COUNT_STORAGE_KEY = 'route-pricing-alert-count';

const getUnderpricedModelCountFromRoutes = (routes) => {
  const keys = new Set();
  (Array.isArray(routes) ? routes : []).forEach((route) => {
    if (!routeHasPricingAlert(route)) {
      return;
    }
    const key = String(route.model_sale_key || '').trim();
    if (key) {
      keys.add(key);
    }
  });
  return keys.size;
};

const publishPricingAlertCount = (routes) => {
  if (typeof window === 'undefined') {
    return;
  }
  const count = getUnderpricedModelCountFromRoutes(routes);
  try {
    window.sessionStorage.setItem(
      ROUTE_PRICING_ALERT_COUNT_STORAGE_KEY,
      String(count)
    );
  } catch {}
  window.dispatchEvent(
    new CustomEvent(ROUTE_PRICING_ALERT_COUNT_EVENT, {
      detail: { count },
    })
  );
};

const getPreferredGroupedModelName = (aliases, fallbackName) => {
  const normalizedAliases = aliases
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  const fallback = String(fallbackName || '').trim();
  if (normalizedAliases.length === 0) {
    return fallback || 'unknown';
  }

  if (fallback.startsWith('claude-')) {
    const datedClaudeVariants = normalizedAliases
      .filter((item) =>
        /^claude-.*(?:-|_)(20\d{6}|\d{8}|20\d{2}-\d{2}-\d{2})$/i.test(item)
      )
      .sort((a, b) => b.localeCompare(a, 'en'));
    if (datedClaudeVariants.length > 0) {
      return datedClaudeVariants[0];
    }
  }

  if (normalizedAliases.includes(fallback)) {
    return fallback;
  }

  return normalizedAliases[0];
};

const getRouteHealth = (route) => {
  const providerUp = Number(route.provider_status) === 1;
  const tokenUp = Number(route.token_status) === 1;
  if (providerUp && tokenUp) {
    return { color: 'green', label: '链路可用' };
  }
  if (!providerUp && !tokenUp) {
    return { color: 'red', label: '供应商/Token停用' };
  }
  if (!providerUp) {
    return { color: 'red', label: '供应商停用' };
  }
  return { color: 'red', label: 'Token停用' };
};

const computeShareByPriority = (rows) => {
  const maxScoreByPriority = {};
  rows.forEach((row) => {
    if (!row.enabled) return;
    const score = Number(row.value_score);
    const safeScore = Number.isFinite(score) && score > 0 ? score : 0;
    const key = String(row.priority);
    maxScoreByPriority[key] = Math.max(maxScoreByPriority[key] || 0, safeScore);
  });

  const contributionById = {};
  const sumByPriority = {};
  rows.forEach((row) => {
    if (!row.enabled) return;
    const base = Math.max(0, Number(row.weight || 0) + 10);
    const score = Number(row.value_score);
    const safeScore = Number.isFinite(score) && score > 0 ? score : 0;
    const configuredBaseFactor = Number(row.base_weight_factor);
    const configuredValueFactor = Number(row.value_score_factor);
    const baseFactor =
      Number.isFinite(configuredBaseFactor) && configuredBaseFactor >= 0
        ? configuredBaseFactor
        : 0.2;
    const valueFactor =
      Number.isFinite(configuredValueFactor) && configuredValueFactor >= 0
        ? configuredValueFactor
        : 0.8;
    const configuredHealthMultiplier = Number(row.health_multiplier);
    const healthAdjustmentEnabled = row.health_adjustment_enabled === true;
    const healthSampleCount = Number(row.health_sample_count);
    const healthMinSamples = Number(row.health_min_samples);
    const hasEnoughHealthSamples =
      Number.isFinite(healthSampleCount) &&
      Number.isFinite(healthMinSamples) &&
      healthMinSamples > 0
        ? healthSampleCount >= healthMinSamples
        : true;
    const healthMultiplier =
      healthAdjustmentEnabled &&
      hasEnoughHealthSamples &&
      Number.isFinite(configuredHealthMultiplier) &&
      configuredHealthMultiplier > 0
        ? configuredHealthMultiplier
        : 1;
    const key = String(row.priority);
    const maxScore = maxScoreByPriority[key] || 0;
    let contribution = base;
    if (base > 0 && maxScore > 0) {
      const normalized = Math.max(0, Math.min(1, safeScore / maxScore));
      contribution = base * (baseFactor + normalized * valueFactor);
    }
    contribution *= healthMultiplier;
    contributionById[getRouteRowKey(row)] = contribution;
    sumByPriority[row.priority] =
      (sumByPriority[row.priority] || 0) + contribution;
  });

  return rows.map((row) => {
    if (!row.enabled) {
      return { ...row, effective_share_percent: null };
    }
    const contribution = contributionById[getRouteRowKey(row)] || 0;
    const total = sumByPriority[row.priority] || 0;
    if (contribution <= 0 || total <= 0) {
      return { ...row, effective_share_percent: null };
    }
    return { ...row, effective_share_percent: (contribution / total) * 100 };
  });
};

const ModelRoutesTable = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTokenCategories, setSavingTokenCategories] = useState({});
  const [searchKeyword, setSearchKeyword] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedModelKey, setSelectedModelKey] = useState('');
  const [drafts, setDrafts] = useState({});
  const [salePriceDrafts, setSalePriceDrafts] = useState({});
  const [saleSaving, setSaleSaving] = useState(false);
  const [batchPriority, setBatchPriority] = useState('');
  const [batchWeight, setBatchWeight] = useState('');
  const [batchEnabled, setBatchEnabled] = useState('keep');
  const [batchCooldownImpact, setBatchCooldownImpact] = useState('keep');
  const [sortMode, setSortMode] = useState('name');
  const [changedOnly, setChangedOnly] = useState(false);
  const [detailChangedOnly, setDetailChangedOnly] = useState(false);
  const [ultraCompact, setUltraCompact] = useState(true);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [pricingHistory, setPricingHistory] = useState(null);
  const [pricingHistoryLoading, setPricingHistoryLoading] = useState(false);
  const [channelVariants, setChannelVariants] = useState([]);
  const [variantOverrideDrafts, setVariantOverrideDrafts] = useState({});
  const [variantSaving, setVariantSaving] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [channelVariantError, setChannelVariantError] = useState(null);
  const [bootstrapStage, setBootstrapStage] = useState('idle');

  const routeMap = useMemo(() => {
    const map = {};
    routes.forEach((route) => {
      map[getRouteRowKey(route)] = route;
    });
    return map;
  }, [routes]);

  const dirtyCount = useMemo(() => Object.keys(drafts).length, [drafts]);
  const saleDirtyCount = useMemo(
    () => Object.keys(salePriceDrafts).length,
    [salePriceDrafts]
  );
  const underpricedRouteCount = useMemo(
    () => getUnderpricedModelCountFromRoutes(routes),
    [routes]
  );

  const providerOptions = useMemo(() => {
    const map = new Map();
    routes.forEach((route) => {
      const key = String(route.provider_id);
      if (!map.has(key)) {
        map.set(key, route.provider_name || `供应商 #${route.provider_id}`);
      }
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'zh-Hans-CN'));
  }, [routes]);

  const loadOverview = useCallback(async (options = {}) => {
    const showSpinner = options?.showSpinner !== false;
    const attempt = Number(options?.attempt || 1);
    if (showSpinner) {
      setLoading(true);
    }
    setBootstrapStage('loading_overview');
    setOverviewError(null);
    try {
      const res = await API.get('/api/route/overview', {
        timeout: ROUTE_OVERVIEW_TIMEOUT_MS,
      });
      const { success, data, message, error_kind: errorKind } = res.data || {};
      if (!success) {
        if (
          attempt < 2 &&
          (errorKind === 'timeout' || errorKind === 'network')
        ) {
          await sleep(ROUTE_OVERVIEW_RETRY_DELAY_MS);
          return loadOverview({
            ...options,
            showSpinner,
            attempt: attempt + 1,
          });
        }
        const nextError = normalizeAppError(
          new Error(
            attempt > 1 && (errorKind === 'timeout' || errorKind === 'network')
              ? `加载路由总览失败，已自动重试 ${attempt - 1} 次：${
                  message || '请求失败'
                }`
              : message || '加载路由总览失败'
          ),
          {
            stage: 'route_overview',
            path: '/routes',
            endpoint: '/api/route/overview',
            kind: errorKind,
          }
        );
        setOverviewError(nextError);
        showError(nextError.appDisplayMessage);
        return false;
      }
      const nextRoutes = Array.isArray(data) ? data : [];
      setRoutes(nextRoutes);
      publishPricingAlertCount(nextRoutes);
      setOverviewError(null);
      setBootstrapStage('overview_ready');
      return true;
    } catch (e) {
      const retryable =
        e?.code === 'ECONNABORTED' ||
        String(e?.message || '')
          .toLowerCase()
          .includes('timeout') ||
        String(e?.message || '')
          .toLowerCase()
          .includes('network');
      if (attempt < 2 && retryable) {
        await sleep(ROUTE_OVERVIEW_RETRY_DELAY_MS);
        return loadOverview({ ...options, showSpinner, attempt: attempt + 1 });
      }
      const nextError = normalizeAppError(e, {
        stage: 'route_overview',
        path: '/routes',
        endpoint: '/api/route/overview',
        displayMessage:
          attempt > 1 && retryable
            ? `加载路由总览失败，已自动重试 ${attempt - 1} 次`
            : undefined,
      });
      setOverviewError(nextError);
      showError(nextError.appDisplayMessage);
      return false;
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  }, []);

  const loadChannelVariants = useCallback(async () => {
    setBootstrapStage('loading_channel_variants');
    setChannelVariantError(null);
    try {
      const res = await API.get('/api/route/channel-variant', {
        timeout: ROUTE_VARIANT_TIMEOUT_MS,
      });
      const { success, data, message, error_kind: errorKind } = res.data || {};
      if (success) {
        setChannelVariants(Array.isArray(data) ? data : []);
        setChannelVariantError(null);
        setBootstrapStage('ready');
        return true;
      }
      setChannelVariantError(
        normalizeAppError(new Error(message || '加载分裂配置失败'), {
          stage: 'route_channel_variant',
          path: '/routes',
          endpoint: '/api/route/channel-variant',
          kind: errorKind,
        })
      );
      return false;
    } catch (e) {
      setChannelVariantError(
        normalizeAppError(e, {
          stage: 'route_channel_variant',
          path: '/routes',
          endpoint: '/api/route/channel-variant',
        })
      );
      return false;
    }
  }, []);

  // Parse variant overrides from JSON string into usable object
  const getVariantOverrides = useCallback((entry) => {
    if (!entry?.isChannelVariant) return {};
    try {
      return JSON.parse(entry.channelVariantOverrides || '{}');
    } catch {
      return {};
    }
  }, []);

  const updateVariantOverride = useCallback((routeId, field, value) => {
    setVariantOverrideDrafts((prev) => {
      const next = { ...prev };
      if (!next[routeId]) next[routeId] = {};
      next[routeId] = { ...next[routeId], [field]: value };
      return next;
    });
  }, []);

  const loadPricingHistory = useCallback(async (modelKey) => {
    const normalizedKey = String(modelKey || '').trim();
    if (!normalizedKey) {
      setPricingHistory(null);
      return;
    }
    setPricingHistoryLoading(true);
    try {
      const res = await API.get('/api/route/pricing-history', {
        params: { model_key: normalizedKey },
      });
      const { success, data, message } = res.data || {};
      if (!success) {
        setPricingHistory(null);
        showError(message || '加载价格历史失败');
        return;
      }
      setPricingHistory(data || null);
    } catch (error) {
      setPricingHistory(null);
      showError('加载价格历史失败');
    } finally {
      setPricingHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      await loadOverview();
      if (cancelled) {
        return;
      }
      await loadChannelVariants();
    };
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadChannelVariants, loadOverview]);

  const filteredRoutes = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    return routes.filter((route) => {
      if (
        providerFilter !== 'all' &&
        String(route.provider_id) !== providerFilter
      ) {
        return false;
      }
      if (statusFilter === 'enabled' && !route.enabled) {
        return false;
      }
      if (statusFilter === 'disabled' && route.enabled) {
        return false;
      }
      if (!keyword) {
        return true;
      }
      const model = String(route.model_name || '').toLowerCase();
      const displayModel = String(route.display_model_name || '').toLowerCase();
      const canonical = normalizeModelName(route.model_name);
      const provider = String(route.provider_name || '').toLowerCase();
      const group = String(route.token_group_name || '').toLowerCase();
      return (
        model.includes(keyword) ||
        displayModel.includes(keyword) ||
        canonical.includes(keyword) ||
        provider.includes(keyword) ||
        group.includes(keyword)
      );
    });
  }, [providerFilter, routes, searchKeyword, statusFilter]);

  const modelEntries = useMemo(() => {
    const grouped = {};
    filteredRoutes.forEach((route) => {
      const routeRowKey = getRouteRowKey(route);
      const draft = drafts[routeRowKey];
      const merged = draft ? { ...route, ...draft } : { ...route };
      const displayModelName = String(merged.display_model_name || '').trim();
      const rawModelName = String(merged.model_name || '').trim();
      const groupKey =
        String(merged.model_sale_key || '').trim() ||
        displayModelName ||
        normalizeModelName(rawModelName) ||
        rawModelName ||
        'unknown';
      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          groupKey,
          routes: [],
          aliases: new Set(),
          displayNames: new Set(),
        };
      }
      grouped[groupKey].routes.push(merged);
      if (rawModelName) {
        grouped[groupKey].aliases.add(rawModelName);
      }
      if (displayModelName) {
        grouped[groupKey].displayNames.add(displayModelName);
      }
    });

    const entries = Object.values(grouped).map((bucket) => {
      const modelRoutes = bucket.routes;
      const aliases = [...bucket.aliases].sort((a, b) =>
        a.localeCompare(b, 'zh-Hans-CN')
      );
      const displayNames = [...bucket.displayNames].sort((a, b) =>
        a.localeCompare(b, 'zh-Hans-CN')
      );
      const withShare = computeShareByPriority(modelRoutes).sort((a, b) => {
        if (b.priority !== a.priority) return b.priority - a.priority;
        if (a.provider_name !== b.provider_name)
          return String(a.provider_name).localeCompare(
            String(b.provider_name),
            'zh-Hans-CN'
          );
        return a.id - b.id;
      });

      const providerCount = new Set(withShare.map((r) => r.provider_id)).size;
      const enabledCount = withShare.filter((r) => r.enabled).length;
      const dirtyRows = withShare.filter((r) =>
        Boolean(drafts[getRouteRowKey(r)])
      ).length;
      const modelPricingAlert = withShare.some((route) =>
        routeHasPricingAlert(route)
      );
      let minPromptPrice = null;
      let minPerCallPrice = null;

      withShare.forEach((route) => {
        const prompt = getPromptPriceUSDForDisplay(route);
        const perCall = Number(route.per_call_price);
        if (route.billing_type === 'per_token' && Number.isFinite(prompt)) {
          if (minPromptPrice === null || prompt < minPromptPrice)
            minPromptPrice = prompt;
        }
        if (route.billing_type === 'per_call' && Number.isFinite(perCall)) {
          if (minPerCallPrice === null || perCall < minPerCallPrice)
            minPerCallPrice = perCall;
        }
      });

      const isVariantEntry = withShare.some(
        (route) => Number(route.channel_variant_id) > 0
      );
      const preferredModelName = isVariantEntry
        ? bucket.groupKey
        : getPreferredGroupedModelName(
            displayNames.length > 0 ? displayNames : aliases,
            bucket.groupKey
          );
      const priceSourceRoute = withShare[0] || null;

      return {
        groupKey: bucket.groupKey,
        modelName: preferredModelName,
        aliases,
        aliasCount: aliases.length,
        routes: withShare,
        routeCount: withShare.length,
        providerCount,
        enabledCount,
        dirtyRows,
        underpricedCount: modelPricingAlert ? 1 : 0,
        modelPricingAlert,
        minPromptPrice,
        minPerCallPrice,
        manualPromptPriceCNYPer1M: Number(
          priceSourceRoute?.manual_prompt_price_cny_per_1m || 0
        ),
        manualCacheReadPriceCNYPer1M: Number(
          priceSourceRoute?.manual_cache_read_price_cny_per_1m || 0
        ),
        manualCacheCreatePriceCNYPer1M: Number(
          priceSourceRoute?.manual_cache_create_price_cny_per_1m || 0
        ),
        manualCompletionPriceCNYPer1M: Number(
          priceSourceRoute?.manual_completion_price_cny_per_1m || 0
        ),
        manualPerCallPriceCNY: Number(
          priceSourceRoute?.manual_per_call_price_cny || 0
        ),
        modelRemark: String(priceSourceRoute?.model_remark || '').trim(),
      };
    });

    const filteredEntries = changedOnly
      ? entries.filter((entry) => entry.dirtyRows > 0)
      : entries;
    return filteredEntries.sort((a, b) => {
      if (sortMode === 'cheapest_prompt') {
        const cmp = compareNullableNumber(a.minPromptPrice, b.minPromptPrice);
        if (cmp !== 0) return cmp;
      } else if (sortMode === 'cheapest_call') {
        const cmp = compareNullableNumber(a.minPerCallPrice, b.minPerCallPrice);
        if (cmp !== 0) return cmp;
      } else if (sortMode === 'dirty_first') {
        if (b.dirtyRows !== a.dirtyRows) return b.dirtyRows - a.dirtyRows;
        if (b.routeCount !== a.routeCount) return b.routeCount - a.routeCount;
      }
      return a.modelName.localeCompare(b.modelName, 'zh-Hans-CN');
    });
  }, [changedOnly, drafts, filteredRoutes, sortMode]);

  // Tag variant entries using channel_variant_id from backend overview data
  const modelEntriesWithVariants = useMemo(() => {
    const variantById = {};
    channelVariants.forEach((v) => {
      variantById[v.id] = v;
    });
    return modelEntries.map((entry) => {
      // Check if any route in this group has a channel_variant_id
      const variantId = entry.routes?.[0]?.channel_variant_id;
      if (!variantId) return entry;
      const v = variantById[variantId];
      if (!v) return entry;
      return {
        ...entry,
        isChannelVariant: true,
        channelVariantId: v.id,
        channelVariantChannel: v.channel,
        channelVariantBaseName: v.model_name,
        channelVariantOverrides: v.route_overrides,
      };
    });
  }, [modelEntries, channelVariants]);

  useEffect(() => {
    if (modelEntriesWithVariants.length === 0) {
      setSelectedModelKey('');
      return;
    }
    const exists = modelEntriesWithVariants.some(
      (item) => item.groupKey === selectedModelKey
    );
    if (!exists) {
      setSelectedModelKey(modelEntriesWithVariants[0].groupKey);
    }
  }, [modelEntriesWithVariants, selectedModelKey]);

  const selectedEntry = useMemo(() => {
    return (
      modelEntriesWithVariants.find(
        (entry) => entry.groupKey === selectedModelKey
      ) || null
    );
  }, [modelEntriesWithVariants, selectedModelKey]);

  useEffect(() => {
    const groupKey = String(selectedEntry?.groupKey || '').trim();
    if (!groupKey) {
      setPricingHistory(null);
      return;
    }
    loadPricingHistory(groupKey);
  }, [loadPricingHistory, selectedEntry?.groupKey]);

  const selectedVisibleRoutes = useMemo(() => {
    if (!selectedEntry) {
      return [];
    }
    const isVariant = selectedEntry.isChannelVariant === true;
    return detailChangedOnly
      ? selectedEntry.routes.filter((route) =>
          isVariant
            ? Boolean(variantOverrideDrafts[getBaseRouteId(route)])
            : Boolean(drafts[getRouteRowKey(route)])
        )
      : selectedEntry.routes;
  }, [detailChangedOnly, drafts, selectedEntry, variantOverrideDrafts]);

  const saveVariantOverrides = useCallback(async () => {
    if (!selectedEntry?.isChannelVariant) return;
    setVariantSaving(true);
    try {
      const existing = getVariantOverrides(selectedEntry);
      const merged = { ...existing };
      Object.entries(variantOverrideDrafts).forEach(([routeId, draft]) => {
        const id = Number(routeId);
        if (!merged[id]) merged[id] = {};
        if (draft.priority !== undefined) merged[id].priority = draft.priority;
        if (draft.weight !== undefined) merged[id].weight = draft.weight;
        if (draft.enabled !== undefined) merged[id].enabled = draft.enabled;
      });
      const res = await API.put(
        `/api/route/channel-variant/${selectedEntry.channelVariantId}/overrides`,
        { route_overrides: merged }
      );
      if (res.data.success) {
        showSuccess('分裂体覆盖配置已保存');
        setVariantOverrideDrafts({});
        await loadChannelVariants();
        await loadOverview({ showSpinner: false });
      } else {
        showError(res.data.message || '保存失败');
      }
    } catch (e) {
      showError('保存失败');
    } finally {
      setVariantSaving(false);
    }
  }, [
    getVariantOverrides,
    loadChannelVariants,
    loadOverview,
    selectedEntry,
    variantOverrideDrafts,
  ]);

  const selectedGroupedRoutes = useMemo(() => {
    if (selectedVisibleRoutes.length === 0) {
      return [];
    }
    const grouped = {};
    selectedVisibleRoutes.forEach((route) => {
      const key = String(route.priority);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(route);
    });
    return Object.entries(grouped)
      .map(([priority, items]) => ({
        priority: Number(priority),
        total: items.length,
        enabled: items.filter((item) => item.enabled).length,
        bestValueScore: items.reduce((max, item) => {
          if (!item.enabled) return max;
          const score = Number(item.value_score);
          if (!Number.isFinite(score) || score <= 0) return max;
          return Math.max(max, score);
        }, 0),
        routes: items,
      }))
      .sort((a, b) => b.priority - a.priority);
  }, [selectedVisibleRoutes]);

  const selectedCostSummaries = useMemo(() => {
    if (selectedGroupedRoutes.length === 0) {
      return [];
    }

    let activationProbability = 1;
    return selectedGroupedRoutes.map((group, index) => {
      const withShare = computeShareByPriority(
        group.routes.map((route) => ({ ...route }))
      );
      const summary = {
        priority: group.priority,
        label: index === 0 ? '主层成本' : '降级层条件成本',
        activationProbability,
        layerFailureProbability: null,
        failureEstimateAvailable: false,
        weightedCacheReadUnitUSD: 0,
        weightedCacheCreateUnitUSD: 0,
        weightedPromptCostCNY: 0,
        weightedCacheReadCostCNY: 0,
        weightedCacheCreateCostCNY: 0,
        weightedCompletionCostCNY: 0,
        weightedPerCallCostCNY: 0,
        promptShareCovered: 0,
        cacheReadShareCovered: 0,
        cacheCreateShareCovered: 0,
        completionShareCovered: 0,
        perCallShareCovered: 0,
        enabledShare: 0,
        routeCount: withShare.length,
      };
      let enabledRoutes = 0;
      let failureProbability = 1;
      let failureEstimateAvailable = activationProbability !== null;

      withShare.forEach((route) => {
        const share = Number(route.effective_share_percent);
        if (!Number.isFinite(share) || share <= 0) {
          return;
        }
        enabledRoutes += 1;
        summary.enabledShare += share;
        const rate = parsePositiveNumber(route.effective_recharge_cny_per_usd);
        if (rate <= 0) {
          // Cost coverage may be missing, but this should not block downgrade probability estimation.
        } else {
          const promptUSD = getPromptPriceUSDForDisplay(route);
          const completionUSD = Number(route.completion_price_per_1m);
          const perCallUSD = Number(route.per_call_price);
          const cacheReadUnitUSD = Number(route.cache_read_price_per_1m);
          const cacheCreateUnitUSD = Number(route.cache_create_price_per_1m);

          const promptCNY = convertUSDToCNY(promptUSD, rate);
          if (promptCNY !== null) {
            summary.weightedPromptCostCNY += (promptCNY * share) / 100;
            summary.promptShareCovered += share;
          }
          if (Number.isFinite(cacheReadUnitUSD) && cacheReadUnitUSD > 0) {
            summary.weightedCacheReadUnitUSD +=
              (cacheReadUnitUSD * share) / 100;
          }
          if (Number.isFinite(cacheCreateUnitUSD) && cacheCreateUnitUSD > 0) {
            summary.weightedCacheCreateUnitUSD +=
              (cacheCreateUnitUSD * share) / 100;
          }
          const cacheReadCNY = convertUSDToCNY(
            route.effective_cache_read_price_per_1m,
            rate
          );
          if (cacheReadCNY !== null) {
            summary.weightedCacheReadCostCNY += (cacheReadCNY * share) / 100;
            summary.cacheReadShareCovered += share;
          }
          const cacheCreateCNY = convertUSDToCNY(
            route.effective_cache_create_price_per_1m,
            rate
          );
          if (cacheCreateCNY !== null) {
            summary.weightedCacheCreateCostCNY +=
              (cacheCreateCNY * share) / 100;
            summary.cacheCreateShareCovered += share;
          }
          const completionCNY = convertUSDToCNY(completionUSD, rate);
          if (completionCNY !== null) {
            summary.weightedCompletionCostCNY += (completionCNY * share) / 100;
            summary.completionShareCovered += share;
          }
          const perCallCNY = convertUSDToCNY(perCallUSD, rate);
          if (perCallCNY !== null) {
            summary.weightedPerCallCostCNY += (perCallCNY * share) / 100;
            summary.perCallShareCovered += share;
          }
        }

        let routeFailProbability = null;
        const failRate = Number(route.health_fail_rate);
        const successRate = Number(route.health_success_rate);
        const sampleCount = Number(route.health_sample_count);
        if (Number.isFinite(sampleCount) && sampleCount > 0) {
          if (Number.isFinite(failRate)) {
            routeFailProbability = Math.max(0, Math.min(1, failRate));
          } else if (Number.isFinite(successRate)) {
            routeFailProbability = Math.max(0, Math.min(1, 1 - successRate));
          }
        }
        if (routeFailProbability === null) {
          failureEstimateAvailable = false;
        } else {
          failureProbability *= routeFailProbability;
        }
      });

      if (enabledRoutes > 0 && failureEstimateAvailable) {
        summary.failureEstimateAvailable = true;
        summary.layerFailureProbability = failureProbability;
        activationProbability *= failureProbability;
      } else {
        activationProbability = null;
      }

      return summary;
    });
  }, [selectedGroupedRoutes]);

  const selectedDowngradeEstimate = useMemo(() => {
    if (selectedCostSummaries.length === 0) {
      return null;
    }
    const estimate = {
      expectedCacheReadUnitUSD: 0,
      expectedCacheCreateUnitUSD: 0,
      expectedPromptCostCNY: 0,
      expectedCacheReadCostCNY: 0,
      expectedCacheCreateCostCNY: 0,
      expectedCompletionCostCNY: 0,
      expectedPerCallCostCNY: 0,
      promptProbabilityCovered: 0,
      cacheReadProbabilityCovered: 0,
      cacheCreateProbabilityCovered: 0,
      completionProbabilityCovered: 0,
      perCallProbabilityCovered: 0,
      estimateAvailable: true,
    };

    selectedCostSummaries.forEach((summary) => {
      if (
        !Number.isFinite(summary.activationProbability) ||
        summary.activationProbability <= 0
      ) {
        estimate.estimateAvailable = false;
        return;
      }
      if (summary.promptShareCovered > 0) {
        estimate.expectedPromptCostCNY +=
          summary.weightedPromptCostCNY * summary.activationProbability;
        estimate.promptProbabilityCovered += summary.activationProbability;
      }
      if (summary.weightedCacheReadUnitUSD > 0) {
        estimate.expectedCacheReadUnitUSD +=
          summary.weightedCacheReadUnitUSD * summary.activationProbability;
      }
      if (summary.weightedCacheCreateUnitUSD > 0) {
        estimate.expectedCacheCreateUnitUSD +=
          summary.weightedCacheCreateUnitUSD * summary.activationProbability;
      }
      if (summary.cacheReadShareCovered > 0) {
        estimate.expectedCacheReadCostCNY +=
          summary.weightedCacheReadCostCNY * summary.activationProbability;
        estimate.cacheReadProbabilityCovered += summary.activationProbability;
      }
      if (summary.cacheCreateShareCovered > 0) {
        estimate.expectedCacheCreateCostCNY +=
          summary.weightedCacheCreateCostCNY * summary.activationProbability;
        estimate.cacheCreateProbabilityCovered += summary.activationProbability;
      }
      if (summary.completionShareCovered > 0) {
        estimate.expectedCompletionCostCNY +=
          summary.weightedCompletionCostCNY * summary.activationProbability;
        estimate.completionProbabilityCovered += summary.activationProbability;
      }
      if (summary.perCallShareCovered > 0) {
        estimate.expectedPerCallCostCNY +=
          summary.weightedPerCallCostCNY * summary.activationProbability;
        estimate.perCallProbabilityCovered += summary.activationProbability;
      }
    });

    return estimate;
  }, [selectedCostSummaries]);

  const selectedOriginalShareMap = useMemo(() => {
    if (!selectedEntry) return {};
    const originalRows = selectedEntry.routes.map((route) => {
      const original = routeMap[getRouteRowKey(route)];
      return original ? { ...original } : { ...route };
    });
    const originalWithShare = computeShareByPriority(originalRows);
    const map = {};
    originalWithShare.forEach((row) => {
      map[getRouteRowKey(row)] = Number(row.effective_share_percent);
    });
    return map;
  }, [routeMap, selectedEntry]);

  const selectedDraftItems = useMemo(() => {
    if (!selectedEntry) {
      return [];
    }
    const selectedRowKeys = new Set(
      selectedEntry.routes.map((route) => getRouteRowKey(route))
    );
    return Object.entries(drafts)
      .filter(([rowKey]) => selectedRowKeys.has(rowKey))
      .map(([rowKey, value]) => ({
        id: getBaseRouteId(routeMap[rowKey]),
        rowKey,
        priority: value.priority,
        weight: value.weight,
        enabled: value.enabled,
        affects_provider_cooldown: value.affects_provider_cooldown,
      }));
  }, [drafts, routeMap, selectedEntry]);

  const otherDraftCount = useMemo(
    () => Math.max(0, Object.keys(drafts).length - selectedDraftItems.length),
    [drafts, selectedDraftItems.length]
  );

  const selectedSalePriceDraft = useMemo(() => {
    if (!selectedEntry) {
      return null;
    }
    return salePriceDrafts[selectedEntry.groupKey]
      ? { ...salePriceDrafts[selectedEntry.groupKey] }
      : createModelSaleDraftFromEntry(selectedEntry);
  }, [salePriceDrafts, selectedEntry]);

  const selectedSalePriceDirty = useMemo(() => {
    if (!selectedEntry || !selectedSalePriceDraft) {
      return false;
    }
    return !modelSaleDraftUnchanged(selectedEntry, selectedSalePriceDraft);
  }, [selectedEntry, selectedSalePriceDraft]);

  const selectedModelPricingView = useMemo(() => {
    const route = selectedEntry?.routes?.[0];
    if (!route) {
      return null;
    }

    const promptAggregate = parsePositiveNumber(
      route.model_aggregate_prompt_cost_cny_per_1m
    );
    const cacheReadAggregate = parsePositiveNumber(
      route.model_aggregate_cache_read_cost_cny_per_1m
    );
    const cacheCreateAggregate = parsePositiveNumber(
      route.model_aggregate_cache_create_cost_cny_per_1m
    );
    const completionAggregate = parsePositiveNumber(
      route.model_aggregate_completion_cost_cny_per_1m
    );
    const perCallAggregate = parsePositiveNumber(
      route.model_aggregate_per_call_cost_cny
    );
    const cacheReadPrice =
      parsePositiveNumber(
        selectedDowngradeEstimate?.expectedCacheReadUnitUSD
      ) || parsePositiveNumber(route.cache_read_price_per_1m);
    const cacheCreatePrice =
      parsePositiveNumber(
        selectedDowngradeEstimate?.expectedCacheCreateUnitUSD
      ) || parsePositiveNumber(route.cache_create_price_per_1m);
    const basis = String(route.model_recommendation_basis || '').trim();

    const promptRecommended = parsePositiveNumber(
      route.model_recommended_prompt_price_cny_per_1m
    );
    const cacheReadRecommended = parsePositiveNumber(
      route.model_recommended_cache_read_price_cny_per_1m
    );
    const cacheCreateRecommended = parsePositiveNumber(
      route.model_recommended_cache_create_price_cny_per_1m
    );
    const completionRecommended = parsePositiveNumber(
      route.model_recommended_completion_price_cny_per_1m
    );
    const perCallRecommended = parsePositiveNumber(
      route.model_recommended_per_call_price_cny
    );

    const draftPrompt = parsePositiveNumber(
      selectedSalePriceDraft?.prompt_price_cny_per_1m
    );
    const draftCacheRead = parsePositiveNumber(
      selectedSalePriceDraft?.cache_read_price_cny_per_1m
    );
    const draftCacheCreate = parsePositiveNumber(
      selectedSalePriceDraft?.cache_create_price_cny_per_1m
    );
    const draftCompletion = parsePositiveNumber(
      selectedSalePriceDraft?.completion_price_cny_per_1m
    );
    const draftPerCall = parsePositiveNumber(
      selectedSalePriceDraft?.per_call_price_cny
    );
    const persistedAlert =
      isPriceBelowRecommended(
        selectedEntry?.manualPromptPriceCNYPer1M,
        promptRecommended
      ) ||
      isPriceBelowRecommended(
        selectedEntry?.manualCacheReadPriceCNYPer1M,
        cacheReadRecommended
      ) ||
      isPriceBelowRecommended(
        selectedEntry?.manualCacheCreatePriceCNYPer1M,
        cacheCreateRecommended
      ) ||
      isPriceBelowRecommended(
        selectedEntry?.manualCompletionPriceCNYPer1M,
        completionRecommended
      ) ||
      isPriceBelowRecommended(
        selectedEntry?.manualPerCallPriceCNY,
        perCallRecommended
      );
    const draftAlert =
      isPriceBelowRecommended(draftPrompt, promptRecommended) ||
      isPriceBelowRecommended(draftCacheRead, cacheReadRecommended) ||
      isPriceBelowRecommended(draftCacheCreate, cacheCreateRecommended) ||
      isPriceBelowRecommended(draftCompletion, completionRecommended) ||
      isPriceBelowRecommended(draftPerCall, perCallRecommended);

    return {
      billingType: String(route.billing_type || '').trim(),
      promptRecommended,
      cacheReadRecommended,
      cacheCreateRecommended,
      completionRecommended,
      perCallRecommended,
      promptAggregate,
      cacheReadAggregate,
      cacheCreateAggregate,
      completionAggregate,
      perCallAggregate,
      cacheReadPrice,
      cacheCreatePrice,
      basis,
      modelAlert: persistedAlert,
      draftAlert,
    };
  }, [selectedDowngradeEstimate, selectedEntry, selectedSalePriceDraft]);

  const selectedPriceComparisons = useMemo(() => {
    if (!selectedModelPricingView || !selectedSalePriceDraft) {
      return [];
    }
    if (selectedModelPricingView.billingType === 'per_call') {
      const item = buildPriceComparisonItem(
        '按次',
        selectedSalePriceDraft.per_call_price_cny,
        selectedModelPricingView.perCallRecommended,
        '/次'
      );
      return item ? [item] : [];
    }
    return [
      buildPriceComparisonItem(
        '输入',
        selectedSalePriceDraft.prompt_price_cny_per_1m,
        selectedModelPricingView.promptRecommended
      ),
      buildPriceComparisonItem(
        '缓存读',
        selectedSalePriceDraft.cache_read_price_cny_per_1m,
        selectedModelPricingView.cacheReadRecommended
      ),
      buildPriceComparisonItem(
        '缓存建',
        selectedSalePriceDraft.cache_create_price_cny_per_1m,
        selectedModelPricingView.cacheCreateRecommended
      ),
      buildPriceComparisonItem(
        '输出',
        selectedSalePriceDraft.completion_price_cny_per_1m,
        selectedModelPricingView.completionRecommended
      ),
    ].filter(Boolean);
  }, [selectedModelPricingView, selectedSalePriceDraft]);

  const selectedPriceComparisonState = useMemo(
    () => getPriceComparisonOverallState(selectedPriceComparisons),
    [selectedPriceComparisons]
  );

  const pricingHistoryPoints = useMemo(
    () => (Array.isArray(pricingHistory?.points) ? pricingHistory.points : []),
    [pricingHistory]
  );

  const pricingHistoryEvents = useMemo(
    () => (Array.isArray(pricingHistory?.events) ? pricingHistory.events : []),
    [pricingHistory]
  );

  const pricingHistoryTrend = useMemo(
    () =>
      buildHistoryTrend(pricingHistoryPoints, getHistoryPointTotalRecommended),
    [pricingHistoryPoints]
  );

  const pricingHistoryChartData = useMemo(() => {
    return pricingHistoryPoints.map((point) => ({
      timestamp: point.timestamp,
      label: formatHistoryTimestamp(point.timestamp),
      aggregate: getHistoryPointTotalAggregate(point),
      recommended: getHistoryPointTotalRecommended(point),
    }));
  }, [pricingHistoryPoints]);

  const updateDraft = useCallback(
    (routeRowKey, patch) => {
      setDrafts((prev) => {
        const original = routeMap[routeRowKey];
        if (!original) return prev;
        const baseDraft = prev[routeRowKey]
          ? { ...prev[routeRowKey] }
          : createDraftFromRoute(original);
        const nextDraft = { ...baseDraft, ...patch };
        if (routeDraftUnchanged(original, nextDraft)) {
          const next = { ...prev };
          delete next[routeRowKey];
          return next;
        }
        return { ...prev, [routeRowKey]: nextDraft };
      });
    },
    [routeMap]
  );

  const updateTokenCategories = useCallback(async (tokenId, categories) => {
    if (!tokenId || !Array.isArray(categories) || categories.length === 0) return;
    setSavingTokenCategories((prev) => ({ ...prev, [tokenId]: true }));
    try {
      const res = await API.put(`/api/route/token/${tokenId}/channel-category`, {
        channel_categories: categories,
      });
      const { success, message } = res.data || {};
      if (!success) {
        showError(message || '渠道分组保存失败');
        return;
      }
      const normalized = ['economy', 'official', 'mixed']
        .filter((category) => categories.includes(category))
        .join(',');
      setRoutes((prev) => prev.map((route) =>
        Number(route.provider_token_id) === Number(tokenId)
          ? { ...route, token_channel_categories: normalized }
          : route
      ));
      showSuccess('渠道分组已保存');
    } catch (error) {
      showError(error.message || '渠道分组保存失败');
    } finally {
      setSavingTokenCategories((prev) => {
        const next = { ...prev };
        delete next[tokenId];
        return next;
      });
    }
  }, []);

  const toggleTokenCategory = useCallback((route, category) => {
    const current = String(route.token_channel_categories || 'economy')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    const next = current.includes(category)
      ? current.filter((value) => value !== category)
      : [...current, category];
    if (next.length === 0) {
      showError('请至少保留一个渠道分组');
      return;
    }
    updateTokenCategories(route.provider_token_id, next);
  }, [updateTokenCategories]);

  const adjustDraftNumeric = useCallback(
    (route, field, delta) => {
      const routeRowKey = getRouteRowKey(route);
      setDrafts((prev) => {
        const original = routeMap[routeRowKey];
        if (!original) return prev;
        const baseDraft = prev[routeRowKey]
          ? { ...prev[routeRowKey] }
          : createDraftFromRoute(original);
        const currentValue =
          baseDraft[field] !== undefined
            ? Number(baseDraft[field] || 0)
            : Number(original?.[field] || 0);
        const nextDraft = { ...baseDraft, [field]: currentValue + delta };
        if (routeDraftUnchanged(original, nextDraft)) {
          const next = { ...prev };
          delete next[routeRowKey];
          return next;
        }
        return { ...prev, [routeRowKey]: nextDraft };
      });
    },
    [routeMap]
  );

  const adjustVariantOverrideNumeric = useCallback(
    (entry, route, field, delta) => {
      const baseRouteId = getBaseRouteId(route);
      const baseOverrides = getVariantOverrides(entry);
      setVariantOverrideDrafts((prev) => {
        const next = { ...prev };
        const existingDraft = next[baseRouteId] ? { ...next[baseRouteId] } : {};
        const fallbackValue =
          existingDraft[field] !== undefined
            ? Number(existingDraft[field] || 0)
            : baseOverrides[baseRouteId]?.[field] !== undefined
            ? Number(baseOverrides[baseRouteId][field] || 0)
            : Number(route?.[field] || 0);
        next[baseRouteId] = {
          ...existingDraft,
          [field]: fallbackValue + delta,
        };
        return next;
      });
    },
    [getVariantOverrides]
  );

  const applyBatchToSelectedModel = () => {
    if (!selectedEntry) return;
    if (
      batchPriority === '' &&
      batchWeight === '' &&
      batchEnabled === 'keep' &&
      batchCooldownImpact === 'keep'
    ) {
      showError('请先填写至少一个批量项');
      return;
    }
    if (selectedEntry.isChannelVariant) {
      if (batchCooldownImpact !== 'keep') {
        showError(
          '分裂模型暂不支持批量修改 cooldown 影响，请在主模型路由里设置'
        );
        return;
      }
      const existingOverrides = getVariantOverrides(selectedEntry);
      setVariantOverrideDrafts((prev) => {
        const next = { ...prev };
        selectedEntry.routes.forEach((route) => {
          const baseRouteId = getBaseRouteId(route);
          const current = next[baseRouteId]
            ? { ...next[baseRouteId] }
            : { ...(existingOverrides[baseRouteId] || {}) };
          if (batchPriority !== '') {
            current.priority = parseInteger(batchPriority, 0);
          }
          if (batchWeight !== '') {
            current.weight = parseInteger(batchWeight, 0);
          }
          if (batchEnabled === 'enabled') {
            current.enabled = true;
          } else if (batchEnabled === 'disabled') {
            current.enabled = false;
          }
          if (batchCooldownImpact === 'affects') {
            current.affects_provider_cooldown = true;
          } else if (batchCooldownImpact === 'isolated') {
            current.affects_provider_cooldown = false;
          }
          next[baseRouteId] = current;
        });
        return next;
      });
      showSuccess(
        `已应用到分裂模型 ${selectedEntry.modelName}，请点击“保存覆盖”`
      );
      return;
    }
    setDrafts((prev) => {
      const next = { ...prev };
      selectedEntry.routes.forEach((route) => {
        const routeRowKey = getRouteRowKey(route);
        const original = routeMap[routeRowKey];
        if (!original) return;
        const current = next[routeRowKey]
          ? { ...next[routeRowKey] }
          : createDraftFromRoute(original);
        if (batchPriority !== '') {
          current.priority = parseInteger(batchPriority, 0);
        }
        if (batchWeight !== '') {
          current.weight = parseInteger(batchWeight, 0);
        }
        if (batchEnabled === 'enabled') {
          current.enabled = true;
        } else if (batchEnabled === 'disabled') {
          current.enabled = false;
        }
        if (batchCooldownImpact === 'affects') {
          current.affects_provider_cooldown = true;
        } else if (batchCooldownImpact === 'isolated') {
          current.affects_provider_cooldown = false;
        }
        if (routeDraftUnchanged(original, current)) {
          delete next[routeRowKey];
        } else {
          next[routeRowKey] = current;
        }
      });
      return next;
    });
    showSuccess(`已应用到模型 ${selectedEntry.modelName}，请点击“保存变更”`);
  };

  const updateSelectedSalePriceDraft = useCallback(
    (patch) => {
      if (!selectedEntry) {
        return;
      }
      setSalePriceDrafts((prev) => {
        const baseDraft = prev[selectedEntry.groupKey]
          ? { ...prev[selectedEntry.groupKey] }
          : createModelSaleDraftFromEntry(selectedEntry);
        const nextDraft = { ...baseDraft, ...patch };
        if (modelSaleDraftUnchanged(selectedEntry, nextDraft)) {
          const next = { ...prev };
          delete next[selectedEntry.groupKey];
          return next;
        }
        return { ...prev, [selectedEntry.groupKey]: nextDraft };
      });
    },
    [selectedEntry]
  );

  const saveSelectedModelSalePrice = async () => {
    if (!selectedEntry || !selectedSalePriceDraft) {
      return;
    }
    let payload;
    try {
      payload = {
        model_key: selectedEntry.groupKey,
        prompt_price_cny_per_1m: parseDraftPriceForPayload(
          selectedSalePriceDraft.prompt_price_cny_per_1m,
          '模型输入售价'
        ),
        cache_read_price_cny_per_1m: parseDraftPriceForPayload(
          selectedSalePriceDraft.cache_read_price_cny_per_1m,
          '模型缓存读取售价'
        ),
        cache_create_price_cny_per_1m: parseDraftPriceForPayload(
          selectedSalePriceDraft.cache_create_price_cny_per_1m,
          '模型缓存创建售价'
        ),
        completion_price_cny_per_1m: parseDraftPriceForPayload(
          selectedSalePriceDraft.completion_price_cny_per_1m,
          '模型输出售价'
        ),
        per_call_price_cny: parseDraftPriceForPayload(
          selectedSalePriceDraft.per_call_price_cny,
          '模型按次售价'
        ),
        remark: draftRemarkText(selectedSalePriceDraft.remark),
      };
    } catch (error) {
      showError(error.message || '模型售价格式无效');
      return;
    }
    setSaleSaving(true);
    const res = await API.put('/api/route/model-sale', payload);
    const { success, message } = res.data || {};
    if (success) {
      showSuccess(`已保存模型 ${selectedEntry.modelName} 的售价配置`);
      setSalePriceDrafts((prev) => {
        const next = { ...prev };
        delete next[selectedEntry.groupKey];
        return next;
      });
      await loadOverview({ showSpinner: false });
    } else {
      showError(message || '保存模型售价失败');
    }
    setSaleSaving(false);
  };

  const acceptSuggestedModelSalePrice = async () => {
    if (!selectedEntry || !selectedModelPricingView) {
      return;
    }
    const payload = {
      model_key: selectedEntry.groupKey,
      prompt_price_cny_per_1m: parsePositiveNumber(
        selectedModelPricingView.promptRecommended
      ),
      cache_read_price_cny_per_1m: parsePositiveNumber(
        selectedModelPricingView.cacheReadRecommended
      ),
      cache_create_price_cny_per_1m: parsePositiveNumber(
        selectedModelPricingView.cacheCreateRecommended
      ),
      completion_price_cny_per_1m: parsePositiveNumber(
        selectedModelPricingView.completionRecommended
      ),
      per_call_price_cny: parsePositiveNumber(
        selectedModelPricingView.perCallRecommended
      ),
      remark: draftRemarkText(selectedSalePriceDraft?.remark),
    };
    if (
      payload.prompt_price_cny_per_1m <= 0 &&
      payload.cache_read_price_cny_per_1m <= 0 &&
      payload.cache_create_price_cny_per_1m <= 0 &&
      payload.completion_price_cny_per_1m <= 0 &&
      payload.per_call_price_cny <= 0
    ) {
      showError('当前模型还没有可接受的建议售价');
      return;
    }

    setSaleSaving(true);
    const res = await API.put('/api/route/model-sale', payload);
    const { success, message } = res.data || {};
    if (success) {
      showSuccess(`已按建议售价更新模型 ${selectedEntry.modelName}`);
      setSalePriceDrafts((prev) => {
        const next = { ...prev };
        delete next[selectedEntry.groupKey];
        return next;
      });
      await loadOverview({ showSpinner: false });
    } else {
      showError(message || '接受建议售价失败');
    }
    setSaleSaving(false);
  };

  const saveChanges = async () => {
    let items;
    try {
      const sourceItems = selectedEntry
        ? selectedDraftItems
        : Object.entries(drafts).map(([id, value]) => ({
            id: getBaseRouteId(routeMap[id]),
            rowKey: id,
            priority: value.priority,
            weight: value.weight,
            enabled: value.enabled,
            affects_provider_cooldown: value.affects_provider_cooldown,
          }));
      items = sourceItems.map((item) => ({
        id: Number(item.id),
        rowKey: item.rowKey,
        priority: item.priority,
        weight: item.weight,
        enabled: item.enabled,
        affects_provider_cooldown: item.affects_provider_cooldown,
      }));
    } catch (error) {
      showError(error.message || '参数格式无效');
      return;
    }
    if (items.length === 0) {
      showError('当前没有待保存的修改');
      return;
    }
    const targetModelName = selectedEntry
      ? selectedEntry.modelName
      : '全部模型';
    const confirmed = window.confirm(
      `即将保存 ${items.length} 条路由变更。\n当前操作对象：${targetModelName}\n是否继续？`
    );
    if (!confirmed) {
      return;
    }
    setSaving(true);
    const res = await API.post('/api/route/batch-update', {
      items,
      debug_selected_model: selectedEntry ? selectedEntry.groupKey : '',
      debug_selected_route_ids: selectedEntry
        ? selectedEntry.routes.map((route) => getBaseRouteId(route))
        : [],
      debug_selected_aliases: selectedEntry ? selectedEntry.aliases : [],
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess(`已保存 ${items.length} 条路由变更`);
      setDrafts((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          if (item.rowKey) {
            delete next[item.rowKey];
          }
        });
        return next;
      });
      await loadOverview({ showSpinner: false });
    } else {
      showError(message || '保存失败');
    }
    setSaving(false);
  };

  const rebuildRoutes = async () => {
    const res = await API.post('/api/route/rebuild');
    const { success, message } = res.data;
    if (success) {
      showSuccess('路由重建任务已启动');
    } else {
      showError(message || '重建失败');
    }
  };

  const copyModelName = async (modelName) => {
    const ok = await copy(modelName);
    if (ok) {
      showSuccess(`模型已复制：${modelName}`);
      return;
    }
    showError('复制失败，请检查浏览器剪贴板权限');
  };

  const loadingLabel =
    bootstrapStage === 'loading_channel_variants'
      ? '正在加载分裂配置...'
      : '正在加载路由总览...';

  const noRoutesLoadError =
    !loading && routes.length === 0 && overviewError ? overviewError : null;

  return (
    <>
      <Card
        padding='0'
        className={`routes-table-shell${
          ultraCompact ? ' routes-table-shell-compact' : ''
        }`}
      >
        <div
          className='routes-topbar'
          style={{
            padding: '1rem',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.75rem',
          }}
        >
          <div>
            <div style={{ fontWeight: '600' }}>模型路由总览</div>
            <div
              style={{
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                marginTop: '0.25rem',
              }}
            >
              共 {modelEntries.length} 个模型，{filteredRoutes.length}{' '}
              条路由，路由待保存 {dirtyCount} 条，模型售价待保存{' '}
              {saleDirtyCount} 个，低于建议售价的模型 {underpricedRouteCount} 个
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant='secondary'
              onClick={async () => {
                await loadOverview();
                await loadChannelVariants();
              }}
              icon={RefreshCcw}
              disabled={loading}
            >
              刷新
            </Button>
            <Button variant='outline' onClick={rebuildRoutes} icon={RefreshCcw}>
              重建路由
            </Button>
            <Button
              variant='secondary'
              onClick={() => setDrafts({})}
              icon={RotateCcw}
              disabled={dirtyCount === 0 || saving}
            >
              撤销未保存
            </Button>
            <Button
              variant='primary'
              onClick={
                selectedEntry?.isChannelVariant
                  ? saveVariantOverrides
                  : saveChanges
              }
              icon={Save}
              loading={saving || variantSaving}
              disabled={
                selectedEntry?.isChannelVariant
                  ? Object.keys(variantOverrideDrafts).length === 0
                  : selectedEntry
                  ? selectedDraftItems.length === 0
                  : dirtyCount === 0
              }
            >
              {selectedEntry?.isChannelVariant
                ? `保存覆盖：${selectedEntry.modelName} (${
                    Object.keys(variantOverrideDrafts).length
                  })`
                : selectedEntry
                ? `保存当前模型：${selectedEntry.modelName}`
                : '保存变更'}
            </Button>
          </div>
        </div>

        <div
          className='routes-filterbar'
          style={{
            padding: '1rem',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <div style={{ minWidth: '240px', flex: '1 1 300px' }}>
            <Input
              placeholder='搜索模型 / 供应商 / 分组'
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              icon={Search}
              style={{ margin: 0 }}
              inputStyle={
                ultraCompact
                  ? {
                      paddingTop: '0.45rem',
                      paddingBottom: '0.45rem',
                      fontSize: '0.8rem',
                    }
                  : undefined
              }
            />
          </div>
          <select
            className='routes-inline-select'
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            style={selectStyle}
          >
            <option value='all'>全部供应商</option>
            {providerOptions.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
          <select
            className='routes-inline-select'
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={selectStyle}
          >
            <option value='all'>全部状态</option>
            <option value='enabled'>仅启用路由</option>
            <option value='disabled'>仅禁用路由</option>
          </select>
          <select
            className='routes-inline-select'
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            style={selectStyle}
          >
            <option value='name'>按模型名排序</option>
            <option value='cheapest_prompt'>按最低输入价</option>
            <option value='cheapest_call'>按最低按次价</option>
            <option value='dirty_first'>按改动优先</option>
          </select>
          <label
            className='routes-switch'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}
          >
            <input
              type='checkbox'
              checked={changedOnly}
              onChange={(e) => setChangedOnly(e.target.checked)}
            />
            仅看已改模型
          </label>
          <label
            className='routes-switch'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}
          >
            <input
              type='checkbox'
              checked={ultraCompact}
              onChange={(e) => setUltraCompact(e.target.checked)}
            />
            超紧凑模式
          </label>
        </div>

        {noRoutesLoadError ? (
          <div style={{ padding: '1rem' }}>
            <AppErrorState
              title='路由页加载失败'
              error={noRoutesLoadError}
              onRetry={async () => {
                await loadOverview();
                await loadChannelVariants();
              }}
              onReload={() => window.location.reload()}
            />
          </div>
        ) : null}

        {!noRoutesLoadError && overviewError && routes.length > 0 ? (
          <div style={{ padding: '1rem 1rem 0' }}>
            <AppErrorState
              title='路由总览刷新失败，当前显示的是上次成功加载的数据'
              error={overviewError}
              compact
              onRetry={async () => {
                await loadOverview();
              }}
            />
          </div>
        ) : null}

        {!noRoutesLoadError && channelVariantError ? (
          <div style={{ padding: '1rem 1rem 0' }}>
            <AppErrorState
              title='分裂配置未完整加载'
              error={channelVariantError}
              compact
              onRetry={async () => {
                await loadChannelVariants();
              }}
            />
          </div>
        ) : null}

        {loading ? (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            <Loading label={loadingLabel} minHeight='320px' />
          </div>
        ) : noRoutesLoadError ? null : modelEntriesWithVariants.length === 0 ? (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            当前筛选条件下没有路由数据
          </div>
        ) : (
          <div
            className='routes-workspace'
            style={{
              flex: 1,
              minHeight: 0,
              padding: '0.85rem 1rem 1rem',
              display: 'flex',
              gap: '0.85rem',
              alignItems: 'stretch',
              overflow: 'hidden',
            }}
          >
            <div
              className='routes-model-panel'
              style={{
                flex: '0 0 300px',
                minWidth: '260px',
                maxWidth: '340px',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <div
                className='routes-model-panel-header'
                style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid var(--border-color)',
                  fontWeight: '600',
                }}
              >
                模型列表
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {modelEntriesWithVariants.map((entry) => {
                  const isActive = entry.groupKey === selectedModelKey;
                  return (
                    <button
                      className='routes-model-item'
                      key={entry.groupKey}
                      type='button'
                      onClick={() => {
                        setSelectedModelKey(entry.groupKey);
                        setVariantOverrideDrafts({});
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        borderBottom: '1px solid var(--border-color)',
                        background: isActive
                          ? 'var(--gray-50)'
                          : 'var(--bg-primary)',
                        padding: entry.isChannelVariant
                          ? '0.55rem 1rem 0.55rem 1.8rem'
                          : '0.75rem 1rem',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                          }}
                        >
                          {entry.isChannelVariant && (
                            <span
                              style={{
                                fontSize: '0.65rem',
                                color: 'var(--primary-color)',
                                fontWeight: 600,
                              }}
                            >
                              CH{entry.channelVariantChannel}
                            </span>
                          )}
                          <code
                            className='routes-model-code'
                            style={{
                              fontSize: '0.8rem',
                              backgroundColor: entry.isChannelVariant
                                ? 'var(--primary-50, #eff6ff)'
                                : 'var(--gray-100)',
                              padding: '0.15rem 0.35rem',
                              borderRadius: '0.25rem',
                              color: 'var(--text-primary)',
                            }}
                          >
                            {entry.modelName}
                          </code>
                        </span>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {entry.aliasCount > 1 && (
                            <Badge color='yellow'>
                              别名 {entry.aliasCount}
                            </Badge>
                          )}
                          {entry.dirtyRows > 0 && (
                            <Badge color='yellow'>
                              待保存 {entry.dirtyRows}
                            </Badge>
                          )}
                          {entry.underpricedCount > 0 && (
                            <Badge color='red'>低于建议价</Badge>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          marginTop: '0.5rem',
                          display: 'flex',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Badge color='blue'>路由 {entry.routeCount}</Badge>
                        <Badge color='green'>启用 {entry.enabledCount}</Badge>
                        <Badge color='gray'>供应商 {entry.providerCount}</Badge>
                      </div>
                      {(entry.aliasCount > 0 ||
                        entry.modelName !== entry.groupKey) && (
                        <div
                          style={{
                            marginTop: '0.4rem',
                            fontSize: '0.74rem',
                            color: 'var(--text-secondary)',
                            lineHeight: 1.35,
                          }}
                          title={entry.aliases.join(' / ')}
                        >
                          定价键: {entry.groupKey}
                          {entry.aliases.length > 0
                            ? ` · 别名: ${entry.aliases
                                .slice(0, 2)
                                .join(' / ')}`
                            : ''}
                          {entry.aliasCount > 2 ? ' ...' : ''}
                        </div>
                      )}
                      {entry.modelRemark && (
                        <div
                          style={{
                            marginTop: '0.4rem',
                            fontSize: '0.74rem',
                            color: 'var(--text-secondary)',
                            lineHeight: 1.45,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            whiteSpace: 'pre-wrap',
                          }}
                          title={entry.modelRemark}
                        >
                          备注: {entry.modelRemark}
                        </div>
                      )}
                      <div
                        style={{
                          marginTop: '0.5rem',
                          fontSize: '0.75rem',
                          color: 'var(--text-secondary)',
                          lineHeight: 1.5,
                        }}
                      >
                        <div>
                          最低输入价:{' '}
                          {entry.minPromptPrice === null
                            ? '-'
                            : `${formatPrice(entry.minPromptPrice)} / 1M`}
                        </div>
                        <div>
                          最低按次价:{' '}
                          {entry.minPerCallPrice === null
                            ? '-'
                            : formatPrice(entry.minPerCallPrice)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className='routes-detail-panel'
              style={{
                flex: '1 1 auto',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              {!selectedEntry ? (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    border: '1px dashed var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '2rem',
                    textAlign: 'center',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  请选择左侧模型查看路由详情
                </div>
              ) : (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                  }}
                >
                  <div
                    className='routes-detail-toolbar'
                    style={{
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-lg)',
                      padding: '0.75rem 1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '0.75rem',
                      flexWrap: 'wrap',
                      alignItems: 'flex-start',
                      flexShrink: 0,
                      maxHeight: 'min(42vh, 460px)',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <code
                          className='routes-detail-code'
                          style={{
                            fontSize: '0.9rem',
                            backgroundColor: 'var(--gray-100)',
                            padding: '0.2rem 0.45rem',
                            borderRadius: '0.25rem',
                          }}
                        >
                          {selectedEntry.modelName}
                        </code>
                        <button
                          type='button'
                          onClick={() => copyModelName(selectedEntry.groupKey)}
                          style={{
                            border: 'none',
                            background: 'none',
                            color: 'var(--primary-600)',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            padding: 0,
                          }}
                        >
                          复制定价键
                        </button>
                        {selectedEntry.isChannelVariant && (
                          <button
                            type='button'
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  `确定删除分裂 ${selectedEntry.modelName}？`
                                )
                              )
                                return;
                              try {
                                const res = await API.delete(
                                  `/api/route/channel-variant/${selectedEntry.channelVariantId}`
                                );
                                if (res.data.success) {
                                  showSuccess('分裂已删除');
                                  await loadChannelVariants();
                                  await loadOverview({ showSpinner: false });
                                } else {
                                  showError(res.data.message || '删除失败');
                                }
                              } catch (e) {
                                showError('删除失败');
                              }
                            }}
                            style={{
                              border: 'none',
                              background: 'none',
                              color: 'var(--danger-color)',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              padding: 0,
                            }}
                          >
                            删除分裂
                          </button>
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: '0.35rem',
                          fontSize: '0.78rem',
                          color: 'var(--text-secondary)',
                          lineHeight: 1.4,
                        }}
                      >
                        定价键: {selectedEntry.groupKey}
                        {selectedEntry.aliases.length > 0
                          ? ` · 别名: ${selectedEntry.aliases.join(' / ')}`
                          : ''}
                      </div>
                      {selectedEntry.modelRemark && (
                        <div
                          style={{
                            marginTop: '0.35rem',
                            padding: '0.45rem 0.6rem',
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: 'rgba(148, 163, 184, 0.08)',
                            color: 'var(--text-secondary)',
                            fontSize: '0.78rem',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          备注：{selectedEntry.modelRemark}
                        </div>
                      )}
                      <div
                        style={{
                          marginTop: '0.4rem',
                          display: 'flex',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Badge color='blue'>
                          路由 {selectedEntry.routeCount}
                        </Badge>
                        <Badge color='green'>
                          启用 {selectedEntry.enabledCount}
                        </Badge>
                        <Badge color='gray'>
                          供应商 {selectedEntry.providerCount}
                        </Badge>
                        {selectedEntry.underpricedCount > 0 && (
                          <Badge color='red'>低于建议价</Badge>
                        )}
                        {selectedSalePriceDirty && (
                          <Badge color='orange'>模型售价待保存</Badge>
                        )}
                        {selectedEntry.dirtyRows > 0 && (
                          <Badge color='yellow'>
                            待保存 {selectedEntry.dirtyRows}
                          </Badge>
                        )}
                        {otherDraftCount > 0 && (
                          <Badge color='orange'>
                            其他模型待保存 {otherDraftCount}
                          </Badge>
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: '0.65rem',
                          padding: '0.65rem 0.75rem',
                          borderRadius: 'var(--radius-md)',
                          backgroundColor: 'rgba(16, 185, 129, 0.07)',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          lineHeight: 1.45,
                        }}
                      >
                        <div
                          style={{
                            color: 'var(--text-primary)',
                            fontWeight: 600,
                            marginBottom: '0.35rem',
                          }}
                        >
                          模型统一售价
                        </div>
                        <div style={{ marginBottom: '0.45rem' }}>
                          这里设置的是当前模型的统一对外售价，不区分供应商路由。当前建议售价按“已开启供应商里最高成本”计算；便宜模型默认按
                          2 倍，成本越高倍率越低。
                        </div>
                        {selectedModelPricingView && (
                          <div
                            style={{
                              marginBottom: '0.5rem',
                              padding: '0.55rem 0.65rem',
                              borderRadius: 'var(--radius-md)',
                              backgroundColor: 'rgba(255, 255, 255, 0.55)',
                              border: '1px solid var(--border-color)',
                            }}
                          >
                            {selectedModelPricingView.billingType ===
                            'per_call' ? (
                              <>
                                <div>
                                  综合成本：
                                  {selectedModelPricingView.perCallAggregate > 0
                                    ? `${formatCNYPrice(
                                        selectedModelPricingView.perCallAggregate
                                      )} / 次`
                                    : '-'}
                                </div>
                                <div>
                                  模型建议售价：
                                  {selectedModelPricingView.perCallRecommended >
                                  0
                                    ? `${formatCNYPrice(
                                        selectedModelPricingView.perCallRecommended
                                      )} / 次`
                                    : '-'}
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  综合成本：
                                  {selectedModelPricingView.promptAggregate > 0
                                    ? formatCNYPrice(
                                        selectedModelPricingView.promptAggregate
                                      )
                                    : '-'}{' '}
                                  /{' '}
                                  {selectedModelPricingView.completionAggregate >
                                  0
                                    ? formatCNYPrice(
                                        selectedModelPricingView.completionAggregate
                                      )
                                    : '-'}
                                </div>
                                {(selectedModelPricingView.cacheReadAggregate >
                                  0 ||
                                  selectedModelPricingView.cacheCreateAggregate >
                                    0) && (
                                  <div style={{ marginTop: '0.2rem' }}>
                                    其中缓存读：
                                    {selectedModelPricingView.cacheReadAggregate >
                                    0
                                      ? formatCNYPrice(
                                          selectedModelPricingView.cacheReadAggregate
                                        )
                                      : '-'}{' '}
                                    / 1M · 缓存建：
                                    {selectedModelPricingView.cacheCreateAggregate >
                                    0
                                      ? formatCNYPrice(
                                          selectedModelPricingView.cacheCreateAggregate
                                        )
                                      : '-'}{' '}
                                    / 1M
                                  </div>
                                )}
                                {(selectedModelPricingView.cacheReadPrice > 0 ||
                                  selectedModelPricingView.cacheCreatePrice >
                                    0) && (
                                  <div style={{ marginTop: '0.2rem' }}>
                                    缓存读单价：
                                    {selectedModelPricingView.cacheReadPrice > 0
                                      ? formatPrice(
                                          selectedModelPricingView.cacheReadPrice
                                        )
                                      : '-'}{' '}
                                    / 1M · 缓存建单价：
                                    {selectedModelPricingView.cacheCreatePrice >
                                    0
                                      ? formatPrice(
                                          selectedModelPricingView.cacheCreatePrice
                                        )
                                      : '-'}{' '}
                                    / 1M
                                  </div>
                                )}
                                <div>
                                  模型建议售价：
                                  {selectedModelPricingView.promptRecommended >
                                  0
                                    ? formatCNYPrice(
                                        selectedModelPricingView.promptRecommended
                                      )
                                    : '-'}{' '}
                                  {selectedModelPricingView.cacheReadRecommended >
                                    0 ||
                                  selectedModelPricingView.cacheCreateRecommended >
                                    0 ? (
                                    <span>
                                      /{' '}
                                      {selectedModelPricingView.cacheReadRecommended >
                                      0
                                        ? formatCNYPrice(
                                            selectedModelPricingView.cacheReadRecommended
                                          )
                                        : '-'}{' '}
                                      /{' '}
                                      {selectedModelPricingView.cacheCreateRecommended >
                                      0
                                        ? formatCNYPrice(
                                            selectedModelPricingView.cacheCreateRecommended
                                          )
                                        : '-'}
                                    </span>
                                  ) : null}{' '}
                                  /{' '}
                                  {selectedModelPricingView.completionRecommended >
                                  0
                                    ? formatCNYPrice(
                                        selectedModelPricingView.completionRecommended
                                      )
                                    : '-'}
                                </div>
                              </>
                            )}
                            <div style={{ marginTop: '0.2rem' }}>
                              计算口径：
                              {selectedModelPricingView.basis ===
                              'highest_enabled_route'
                                ? '已开启供应商最高成本'
                                : '已开启供应商最高成本'}
                            </div>
                            {pricingHistoryTrend && (
                              <div
                                style={{
                                  marginTop: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.45rem',
                                  flexWrap: 'wrap',
                                }}
                              >
                                <span>较上次上游快照：</span>
                                <Badge
                                  color={
                                    pricingHistoryTrend.direction === 'up'
                                      ? 'red'
                                      : pricingHistoryTrend.direction === 'down'
                                      ? 'green'
                                      : 'gray'
                                  }
                                >
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '0.25rem',
                                    }}
                                  >
                                    {pricingHistoryTrend.direction === 'up' ? (
                                      <ArrowUpRight size={14} />
                                    ) : pricingHistoryTrend.direction ===
                                      'down' ? (
                                      <ArrowDownRight size={14} />
                                    ) : (
                                      <Minus size={14} />
                                    )}
                                    {pricingHistoryTrend.direction === 'flat'
                                      ? '无变化'
                                      : `${formatSignedCNYPrice(
                                          pricingHistoryTrend.delta
                                        )} ${formatSignedPercent(
                                          pricingHistoryTrend.percent
                                        )}`}
                                  </span>
                                </Badge>
                              </div>
                            )}
                            <div
                              style={{
                                marginTop: '0.25rem',
                                color: getPriceComparisonSummaryColor(
                                  selectedPriceComparisonState
                                ),
                                fontWeight: 600,
                              }}
                            >
                              {getPriceComparisonSummaryLabel(
                                selectedPriceComparisonState
                              )}
                            </div>
                            {selectedPriceComparisons.length > 0 && (
                              <div
                                style={{
                                  marginTop: '0.35rem',
                                  display: 'flex',
                                  gap: '0.35rem',
                                  flexWrap: 'wrap',
                                }}
                              >
                                {selectedPriceComparisons.map((comparison) => (
                                  <Badge
                                    key={comparison.key}
                                    color={getComparisonBadgeColor(
                                      comparison.status
                                    )}
                                  >
                                    {getComparisonSummaryText(comparison)}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <div
                          style={{
                            marginTop: '0.45rem',
                            display: 'flex',
                            gap: '0.5rem',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                          }}
                        >
                          <Button
                            variant='secondary'
                            onClick={acceptSuggestedModelSalePrice}
                            loading={saleSaving}
                            disabled={
                              !selectedModelPricingView ||
                              (parsePositiveNumber(
                                selectedModelPricingView.promptRecommended
                              ) <= 0 &&
                                parsePositiveNumber(
                                  selectedModelPricingView.completionRecommended
                                ) <= 0 &&
                                parsePositiveNumber(
                                  selectedModelPricingView.perCallRecommended
                                ) <= 0)
                            }
                          >
                            接受建议
                          </Button>
                          <Button
                            variant='outline'
                            onClick={() => setHistoryModalOpen(true)}
                            icon={History}
                            disabled={!selectedEntry}
                          >
                            历史
                          </Button>
                          <span style={helperTextStyle}>
                            一键把当前模型售价更新为建议售价；历史里看的是上游进货价变化
                          </span>
                        </div>
                        {String(
                          selectedEntry.routes[0]?.billing_type || ''
                        ).trim() === 'per_call' ? (
                          <div
                            style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <input
                              className='routes-number-input'
                              type='number'
                              min='0'
                              step='0.0001'
                              placeholder='按次售价'
                              value={
                                selectedSalePriceDraft?.per_call_price_cny || ''
                              }
                              onChange={(e) =>
                                updateSelectedSalePriceDraft({
                                  per_call_price_cny: e.target.value,
                                })
                              }
                              style={{
                                ...numberInputStyle,
                                width: '140px',
                                textAlign: 'left',
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <input
                              className='routes-number-input'
                              type='number'
                              min='0'
                              step='0.0001'
                              placeholder='输入售价 / 1M'
                              value={
                                selectedSalePriceDraft?.prompt_price_cny_per_1m ||
                                ''
                              }
                              onChange={(e) =>
                                updateSelectedSalePriceDraft({
                                  prompt_price_cny_per_1m: e.target.value,
                                })
                              }
                              style={{
                                ...numberInputStyle,
                                width: '148px',
                                textAlign: 'left',
                              }}
                            />
                            <input
                              className='routes-number-input'
                              type='number'
                              min='0'
                              step='0.0001'
                              placeholder='缓存读售价 / 1M'
                              value={
                                selectedSalePriceDraft?.cache_read_price_cny_per_1m ||
                                ''
                              }
                              onChange={(e) =>
                                updateSelectedSalePriceDraft({
                                  cache_read_price_cny_per_1m: e.target.value,
                                })
                              }
                              style={{
                                ...numberInputStyle,
                                width: '148px',
                                textAlign: 'left',
                              }}
                            />
                            <input
                              className='routes-number-input'
                              type='number'
                              min='0'
                              step='0.0001'
                              placeholder='缓存建售价 / 1M'
                              value={
                                selectedSalePriceDraft?.cache_create_price_cny_per_1m ||
                                ''
                              }
                              onChange={(e) =>
                                updateSelectedSalePriceDraft({
                                  cache_create_price_cny_per_1m: e.target.value,
                                })
                              }
                              style={{
                                ...numberInputStyle,
                                width: '148px',
                                textAlign: 'left',
                              }}
                            />
                            <input
                              className='routes-number-input'
                              type='number'
                              min='0'
                              step='0.0001'
                              placeholder='输出售价 / 1M'
                              value={
                                selectedSalePriceDraft?.completion_price_cny_per_1m ||
                                ''
                              }
                              onChange={(e) =>
                                updateSelectedSalePriceDraft({
                                  completion_price_cny_per_1m: e.target.value,
                                })
                              }
                              style={{
                                ...numberInputStyle,
                                width: '148px',
                                textAlign: 'left',
                              }}
                            />
                          </div>
                        )}
                        <div style={{ marginTop: '0.6rem' }}>
                          <textarea
                            placeholder='模型备注，可选，保存在模型统一配置里'
                            value={selectedSalePriceDraft?.remark || ''}
                            onChange={(e) =>
                              updateSelectedSalePriceDraft({
                                remark: e.target.value,
                              })
                            }
                            style={textareaStyle}
                          />
                        </div>
                        <div
                          style={{
                            marginTop: '0.5rem',
                            display: 'flex',
                            gap: '0.5rem',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                          }}
                        >
                          <Button
                            variant='primary'
                            onClick={saveSelectedModelSalePrice}
                            loading={saleSaving}
                            disabled={!selectedSalePriceDirty}
                          >
                            保存模型配置
                          </Button>
                          <span style={helperTextStyle}>
                            统一售价和模型备注一起保存；备注会显示在模型列表和详情里
                          </span>
                        </div>
                        <div style={{ marginTop: '0.35rem' }}>
                          当前键：<code>{selectedEntry.groupKey}</code>
                        </div>
                      </div>
                      {selectedCostSummaries.length > 0 && (
                        <div
                          style={{
                            marginTop: '0.6rem',
                            padding: '0.65rem 0.75rem',
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: 'rgba(59, 130, 246, 0.06)',
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary)',
                            lineHeight: 1.45,
                          }}
                        >
                          <div
                            style={{
                              color: 'var(--text-primary)',
                              fontWeight: 600,
                              marginBottom: '0.25rem',
                            }}
                          >
                            分优先级成本估计
                          </div>
                          <div style={{ marginBottom: '0.35rem' }}>
                            推荐售价按成本分段计算：便宜模型接近翻倍，成本越高加价越收敛。
                          </div>
                          {selectedDowngradeEstimate &&
                            (selectedDowngradeEstimate.promptProbabilityCovered >
                              0 ||
                              selectedDowngradeEstimate.completionProbabilityCovered >
                                0 ||
                              selectedDowngradeEstimate.perCallProbabilityCovered >
                                0) && (
                              <div
                                style={{
                                  marginBottom: '0.35rem',
                                  paddingBottom: '0.35rem',
                                  borderBottom:
                                    '1px dashed var(--border-color)',
                                }}
                              >
                                <div
                                  style={{
                                    color: 'var(--text-primary)',
                                    fontWeight: 600,
                                  }}
                                >
                                  分优先级参考成本
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>
                                  这里只展示按优先级分层后的参考成本拆解，不再参与当前模型建议售价。
                                </div>
                                {selectedDowngradeEstimate.promptProbabilityCovered >
                                  0 && (
                                  <div>
                                    输入：
                                    {formatCNYPrice(
                                      selectedDowngradeEstimate.expectedPromptCostCNY
                                    )}{' '}
                                    / 1M{' '}
                                    <span
                                      style={{ color: 'var(--text-secondary)' }}
                                    >
                                      （覆盖{' '}
                                      {formatPercent(
                                        selectedDowngradeEstimate.promptProbabilityCovered *
                                          100
                                      )}
                                      ）
                                    </span>
                                  </div>
                                )}
                                {selectedDowngradeEstimate.cacheReadProbabilityCovered >
                                  0 && (
                                  <div>
                                    缓存读：
                                    {formatCNYPrice(
                                      selectedDowngradeEstimate.expectedCacheReadCostCNY
                                    )}{' '}
                                    / 1M{' '}
                                    <span
                                      style={{ color: 'var(--text-secondary)' }}
                                    >
                                      （覆盖{' '}
                                      {formatPercent(
                                        selectedDowngradeEstimate.cacheReadProbabilityCovered *
                                          100
                                      )}
                                      ）
                                    </span>
                                  </div>
                                )}
                                {selectedDowngradeEstimate.cacheCreateProbabilityCovered >
                                  0 && (
                                  <div>
                                    缓存建：
                                    {formatCNYPrice(
                                      selectedDowngradeEstimate.expectedCacheCreateCostCNY
                                    )}{' '}
                                    / 1M{' '}
                                    <span
                                      style={{ color: 'var(--text-secondary)' }}
                                    >
                                      （覆盖{' '}
                                      {formatPercent(
                                        selectedDowngradeEstimate.cacheCreateProbabilityCovered *
                                          100
                                      )}
                                      ）
                                    </span>
                                  </div>
                                )}
                                {(selectedDowngradeEstimate.expectedCacheReadUnitUSD >
                                  0 ||
                                  selectedDowngradeEstimate.expectedCacheCreateUnitUSD >
                                    0) && (
                                  <div>
                                    缓存读单价：
                                    {selectedDowngradeEstimate.expectedCacheReadUnitUSD >
                                    0
                                      ? formatPrice(
                                          selectedDowngradeEstimate.expectedCacheReadUnitUSD
                                        )
                                      : '-'}{' '}
                                    / 1M · 缓存建单价：
                                    {selectedDowngradeEstimate.expectedCacheCreateUnitUSD >
                                    0
                                      ? formatPrice(
                                          selectedDowngradeEstimate.expectedCacheCreateUnitUSD
                                        )
                                      : '-'}{' '}
                                    / 1M
                                  </div>
                                )}
                                {selectedDowngradeEstimate.completionProbabilityCovered >
                                  0 && (
                                  <div>
                                    输出：
                                    {formatCNYPrice(
                                      selectedDowngradeEstimate.expectedCompletionCostCNY
                                    )}{' '}
                                    / 1M{' '}
                                    <span
                                      style={{ color: 'var(--text-secondary)' }}
                                    >
                                      （覆盖{' '}
                                      {formatPercent(
                                        selectedDowngradeEstimate.completionProbabilityCovered *
                                          100
                                      )}
                                      ）
                                    </span>
                                  </div>
                                )}
                                {selectedDowngradeEstimate.perCallProbabilityCovered >
                                  0 && (
                                  <div>
                                    按次：
                                    {formatCNYPrice(
                                      selectedDowngradeEstimate.expectedPerCallCostCNY
                                    )}{' '}
                                    / 次{' '}
                                    <span
                                      style={{ color: 'var(--text-secondary)' }}
                                    >
                                      （覆盖{' '}
                                      {formatPercent(
                                        selectedDowngradeEstimate.perCallProbabilityCovered *
                                          100
                                      )}
                                      ）
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          {selectedCostSummaries.map((summary) => (
                            <div
                              key={summary.priority}
                              style={{
                                marginTop: '0.45rem',
                                paddingTop: '0.45rem',
                                borderTop: '1px dashed var(--border-color)',
                              }}
                            >
                              <div
                                style={{
                                  color: 'var(--text-primary)',
                                  fontWeight: 600,
                                }}
                              >
                                {summary.label}（优先级 {summary.priority}）
                              </div>
                              {Number.isFinite(
                                summary.activationProbability
                              ) && (
                                <div>
                                  预计命中概率：
                                  {formatPercent(
                                    summary.activationProbability * 100
                                  )}
                                </div>
                              )}
                              {summary.failureEstimateAvailable &&
                                Number.isFinite(
                                  summary.layerFailureProbability
                                ) && (
                                  <div>
                                    本层降级概率：
                                    {formatPercent(
                                      summary.layerFailureProbability * 100
                                    )}
                                  </div>
                                )}
                              {summary.promptShareCovered > 0 && (
                                <div>
                                  输入：
                                  {formatCNYPrice(
                                    summary.weightedPromptCostCNY
                                  )}{' '}
                                  / 1M{' '}
                                  <span
                                    style={{ color: 'var(--text-secondary)' }}
                                  >
                                    （层内覆盖{' '}
                                    {formatPercent(summary.promptShareCovered)}
                                    ）
                                  </span>
                                </div>
                              )}
                              {summary.cacheReadShareCovered > 0 && (
                                <div>
                                  缓存读：
                                  {formatCNYPrice(
                                    summary.weightedCacheReadCostCNY
                                  )}{' '}
                                  / 1M{' '}
                                  <span
                                    style={{ color: 'var(--text-secondary)' }}
                                  >
                                    （层内覆盖{' '}
                                    {formatPercent(
                                      summary.cacheReadShareCovered
                                    )}
                                    ）
                                  </span>
                                </div>
                              )}
                              {summary.cacheCreateShareCovered > 0 && (
                                <div>
                                  缓存建：
                                  {formatCNYPrice(
                                    summary.weightedCacheCreateCostCNY
                                  )}{' '}
                                  / 1M{' '}
                                  <span
                                    style={{ color: 'var(--text-secondary)' }}
                                  >
                                    （层内覆盖{' '}
                                    {formatPercent(
                                      summary.cacheCreateShareCovered
                                    )}
                                    ）
                                  </span>
                                </div>
                              )}
                              {(summary.weightedCacheReadUnitUSD > 0 ||
                                summary.weightedCacheCreateUnitUSD > 0) && (
                                <div>
                                  缓存读单价：
                                  {summary.weightedCacheReadUnitUSD > 0
                                    ? formatPrice(
                                        summary.weightedCacheReadUnitUSD
                                      )
                                    : '-'}{' '}
                                  / 1M · 缓存建单价：
                                  {summary.weightedCacheCreateUnitUSD > 0
                                    ? formatPrice(
                                        summary.weightedCacheCreateUnitUSD
                                      )
                                    : '-'}{' '}
                                  / 1M
                                </div>
                              )}
                              {summary.completionShareCovered > 0 && (
                                <div>
                                  输出：
                                  {formatCNYPrice(
                                    summary.weightedCompletionCostCNY
                                  )}{' '}
                                  / 1M{' '}
                                  <span
                                    style={{ color: 'var(--text-secondary)' }}
                                  >
                                    （层内覆盖{' '}
                                    {formatPercent(
                                      summary.completionShareCovered
                                    )}
                                    ）
                                  </span>
                                </div>
                              )}
                              {summary.perCallShareCovered > 0 && (
                                <div>
                                  按次：
                                  {formatCNYPrice(
                                    summary.weightedPerCallCostCNY
                                  )}{' '}
                                  / 次{' '}
                                  <span
                                    style={{ color: 'var(--text-secondary)' }}
                                  >
                                    （层内覆盖{' '}
                                    {formatPercent(summary.perCallShareCovered)}
                                    ）
                                  </span>
                                </div>
                              )}
                              {summary.promptShareCovered === 0 &&
                                summary.completionShareCovered === 0 &&
                                summary.perCallShareCovered === 0 && (
                                  <div>
                                    当前优先级层还没有可用的真实成本校准数据
                                  </div>
                                )}
                            </div>
                          ))}
                          <div style={{ marginTop: '0.2rem' }}>
                            占比是“优先级层内占比”，不是全局流量占比。低优先级层只在高优先级层全部失败时才会生效。
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      <input
                        className='routes-number-input'
                        type='number'
                        placeholder='批量优先级'
                        value={batchPriority}
                        onChange={(e) => setBatchPriority(e.target.value)}
                        style={numberInputStyle}
                      />
                      <input
                        className='routes-number-input'
                        type='number'
                        placeholder='批量权重'
                        value={batchWeight}
                        onChange={(e) => setBatchWeight(e.target.value)}
                        style={numberInputStyle}
                      />
                      <select
                        className='routes-inline-select'
                        value={batchEnabled}
                        onChange={(e) => setBatchEnabled(e.target.value)}
                        style={selectStyle}
                      >
                        <option value='keep'>状态不变</option>
                        <option value='enabled'>全部启用</option>
                        <option value='disabled'>全部禁用</option>
                      </select>
                      <select
                        className='routes-inline-select'
                        value={batchCooldownImpact}
                        onChange={(e) => setBatchCooldownImpact(e.target.value)}
                        style={selectStyle}
                      >
                        <option value='keep'>Cooldown 影响不变</option>
                        <option value='affects'>失败影响 Provider</option>
                        <option value='isolated'>失败不影响 Provider</option>
                      </select>
                      <label
                        className='routes-switch'
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        <input
                          type='checkbox'
                          checked={detailChangedOnly}
                          onChange={(e) =>
                            setDetailChangedOnly(e.target.checked)
                          }
                        />
                        仅显示已修改路由
                      </label>
                      <Button
                        variant='secondary'
                        onClick={applyBatchToSelectedModel}
                      >
                        应用到当前模型
                      </Button>
                      <Button
                        variant='outline'
                        onClick={async () => {
                          const channelNum =
                            prompt('输入渠道号（正整数，如 1、2）：');
                          if (!channelNum) return;
                          const ch = parseInt(channelNum, 10);
                          if (!ch || ch <= 0) {
                            showError('渠道号必须为正整数');
                            return;
                          }
                          const baseName = selectedEntry?.isChannelVariant
                            ? selectedEntry?.channelVariantBaseName ||
                              selectedEntry?.groupKey
                            : selectedEntry?.groupKey ||
                              selectedEntry?.routes?.[0]?.model_name ||
                              selectedEntry?.modelName;
                          if (!baseName) return;
                          try {
                            const res = await API.post(
                              '/api/route/channel-variant',
                              {
                                model_name: baseName,
                                channel: ch,
                              }
                            );
                            if (res.data.success) {
                              showSuccess(`已创建分裂 ${baseName}--${ch}`);
                              await loadChannelVariants();
                              await loadOverview({ showSpinner: false });
                            } else {
                              showError(res.data.message || '创建分裂失败');
                            }
                          } catch (e) {
                            showError('创建分裂失败');
                          }
                        }}
                      >
                        分裂模型
                      </Button>
                      {selectedEntry?.isChannelVariant &&
                        Object.keys(variantOverrideDrafts).length > 0 && (
                          <Button
                            variant='primary'
                            onClick={saveVariantOverrides}
                            loading={variantSaving}
                          >
                            保存覆盖 (
                            {Object.keys(variantOverrideDrafts).length})
                          </Button>
                        )}
                    </div>
                  </div>

                  <div className='routes-detail-scroller'>
                    <Table
                      tableStyle={{ tableLayout: 'fixed' }}
                      minWidth={ultraCompact ? '860px' : '980px'}
                    >
                      <colgroup>
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '10%' }} />
                      </colgroup>
                      <Thead>
                        <Tr>
                          <Th style={stickyHeaderCellStyle}>供应商 / 令牌</Th>
                          <Th style={stickyHeaderCellStyle}>费用</Th>
                          <Th style={stickyHeaderCellStyle}>渠道分组</Th>
                          <Th style={stickyHeaderCellStyle}>优先级</Th>
                          <Th style={stickyHeaderCellStyle}>权重</Th>
                          <Th style={stickyHeaderCellStyle}>占比</Th>
                          <Th style={stickyHeaderCellStyle}>Cooldown 影响</Th>
                          <Th style={stickyHeaderCellStyle}>状态</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {selectedGroupedRoutes.length === 0 ? (
                          <Tr>
                            <Td
                              colSpan={8}
                              style={{
                                textAlign: 'center',
                                color: 'var(--text-secondary)',
                                ...cellMiddleStyle,
                              }}
                            >
                              当前模型没有符合条件的路由
                            </Td>
                          </Tr>
                        ) : (
                          selectedGroupedRoutes.map((group) => (
                            <React.Fragment key={group.priority}>
                              <Tr style={{ backgroundColor: 'var(--gray-50)' }}>
                                <Td
                                  colSpan={8}
                                  style={{
                                    ...cellMiddleStyle,
                                    paddingTop: '0.95rem',
                                    paddingBottom: '0.95rem',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: '0.5rem',
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: '600',
                                        color: 'var(--text-primary)',
                                      }}
                                    >
                                      优先级 {group.priority}
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: '0.5rem',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Badge color='blue'>
                                        路由 {group.total}
                                      </Badge>
                                      <Badge color='green'>
                                        启用 {group.enabled}
                                      </Badge>
                                    </div>
                                  </div>
                                </Td>
                              </Tr>
                              {group.routes.map((route) => {
                                const routeRowKey = getRouteRowKey(route);
                                const isDirty = Boolean(drafts[routeRowKey]);
                                const share = Number(
                                  route.effective_share_percent
                                );
                                const originalShare = Number(
                                  selectedOriginalShareMap[routeRowKey]
                                );
                                const shareDelta =
                                  Number.isFinite(share) &&
                                  Number.isFinite(originalShare)
                                    ? share - originalShare
                                    : null;
                                const valueScore = Number(route.value_score);
                                const bestValueScore = Number(
                                  group.bestValueScore
                                );
                                const isBestValue =
                                  route.enabled &&
                                  Number.isFinite(valueScore) &&
                                  valueScore > 0 &&
                                  Number.isFinite(bestValueScore) &&
                                  bestValueScore > 0 &&
                                  Math.abs(valueScore - bestValueScore) < 1e-9;
                                const health = getRouteHealth(route);
                                const tokenName = String(
                                  route.token_name || ''
                                ).trim();
                                const tokenGroup = String(
                                  route.token_group_name || ''
                                ).trim();
                                const displayTokenName =
                                  tokenName && tokenName !== tokenGroup
                                    ? tokenName
                                    : '';
                                const providerBalance = String(
                                  route.provider_balance || ''
                                ).trim();
                                const recentUsageCost = Number(
                                  route.recent_usage_cost_usd
                                );
                                const usageWindowHours = Number(
                                  route.usage_window_hours
                                );
                                const healthMultiplier = Number(
                                  route.health_multiplier
                                );
                                const healthSampleCount = Number(
                                  route.health_sample_count
                                );
                                const healthFailRate = Number(
                                  route.health_fail_rate
                                );
                                const healthSuccessRate = Number(
                                  route.health_success_rate
                                );
                                const healthMinSamples = Number(
                                  route.health_min_samples
                                );
                                const faceValueRate = parsePositiveNumber(
                                  route.upstream_face_value_cny_per_usd
                                );
                                const effectiveRechargeRate =
                                  parsePositiveNumber(
                                    route.effective_recharge_cny_per_usd
                                  );
                                const perCallFaceValueCNY = convertUSDToCNY(
                                  route.per_call_price,
                                  faceValueRate
                                );
                                const perCallCostCNY = convertUSDToCNY(
                                  route.per_call_price,
                                  effectiveRechargeRate
                                );
                                const showPerCallReference =
                                  perCallFaceValueCNY !== null &&
                                  perCallCostCNY !== null &&
                                  Math.abs(
                                    perCallFaceValueCNY - perCallCostCNY
                                  ) > 0.00005;
                                const promptPriceUSD =
                                  getPromptPriceUSDForDisplay(route);
                                const promptFaceValueCNY = convertUSDToCNY(
                                  promptPriceUSD,
                                  faceValueRate
                                );
                                const completionFaceValueCNY = convertUSDToCNY(
                                  route.completion_price_per_1m,
                                  faceValueRate
                                );
                                const promptCostCNY = convertUSDToCNY(
                                  promptPriceUSD,
                                  effectiveRechargeRate
                                );
                                const completionCostCNY = convertUSDToCNY(
                                  route.completion_price_per_1m,
                                  effectiveRechargeRate
                                );
                                const showPerTokenReference =
                                  promptFaceValueCNY !== null &&
                                  completionFaceValueCNY !== null &&
                                  promptCostCNY !== null &&
                                  completionCostCNY !== null &&
                                  (Math.abs(
                                    promptFaceValueCNY - promptCostCNY
                                  ) > 0.00005 ||
                                    Math.abs(
                                      completionFaceValueCNY - completionCostCNY
                                    ) > 0.00005);
                                const healthEnabled =
                                  route.health_adjustment_enabled === true;
                                const hasHealthSampleThreshold =
                                  Number.isFinite(healthMinSamples) &&
                                  healthMinSamples > 0;
                                const hasEnoughHealthSamples =
                                  hasHealthSampleThreshold &&
                                  Number.isFinite(healthSampleCount)
                                    ? healthSampleCount >= healthMinSamples
                                    : !hasHealthSampleThreshold;
                                const showHealthEffect =
                                  healthEnabled &&
                                  hasEnoughHealthSamples &&
                                  Number.isFinite(healthMultiplier) &&
                                  healthSampleCount > 0 &&
                                  Math.abs(healthMultiplier - 1) >= 0.0005;
                                const pricingAlert =
                                  routeHasPricingAlert(route);
                                return (
                                  <Tr
                                    key={getRouteRowKey(route)}
                                    style={
                                      isDirty
                                        ? {
                                            backgroundColor:
                                              'rgba(245, 158, 11, 0.06)',
                                          }
                                        : pricingAlert
                                        ? {
                                            backgroundColor:
                                              'rgba(239, 68, 68, 0.05)',
                                          }
                                        : undefined
                                    }
                                  >
                                    <Td style={cellTopStyle}>
                                      <div
                                        style={{
                                          fontWeight: '600',
                                          color: 'var(--text-primary)',
                                          lineHeight: 1.35,
                                        }}
                                      >
                                        {route.provider_name || '未知供应商'}
                                      </div>
                                      {displayTokenName && (
                                        <div
                                          style={{
                                            ...helperTextStyle,
                                            marginTop: '0.2rem',
                                          }}
                                        >
                                          {displayTokenName}
                                        </div>
                                      )}
                                      <div
                                        style={{
                                          ...helperTextStyle,
                                          marginTop: '0.16rem',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        精确模型: {route.model_name || '-'}
                                      </div>
                                      <div
                                        style={{
                                          ...helperTextStyle,
                                          marginTop: '0.2rem',
                                        }}
                                      >
                                        余额: {providerBalance || '-'}
                                      </div>
                                      <div
                                        style={{
                                          ...helperTextStyle,
                                          marginTop: '0.16rem',
                                        }}
                                      >
                                        {Number.isFinite(usageWindowHours) &&
                                        usageWindowHours > 0
                                          ? `${usageWindowHours}h`
                                          : '24h'}
                                        使用:{' '}
                                        {Number.isFinite(recentUsageCost)
                                          ? formatPrice(recentUsageCost, 4)
                                          : '-'}
                                      </div>
                                      <div
                                        style={{
                                          marginTop: '0.35rem',
                                          display: 'flex',
                                          gap: '0.35rem',
                                          alignItems: 'center',
                                          flexWrap: 'wrap',
                                        }}
                                      >
                                        {tokenGroup ? (
                                          <Badge color='gray'>
                                            组: {tokenGroup}
                                          </Badge>
                                        ) : (
                                          <Badge color='gray'>未分组</Badge>
                                        )}
                                        <Badge color={health.color}>
                                          {health.label}
                                        </Badge>
                                        {isDirty && (
                                          <Badge color='yellow'>已修改</Badge>
                                        )}
                                      </div>
                                    </Td>
                                    <Td style={cellTopStyle}>
                                      {route.billing_type === 'per_call' ? (
                                        <div style={{ lineHeight: 1.35 }}>
                                          <div
                                            style={{
                                              color: 'var(--text-primary)',
                                              fontWeight: 600,
                                            }}
                                          >
                                            {convertUSDToCNY(
                                              route.per_call_price,
                                              effectiveRechargeRate
                                            ) !== null
                                              ? `成本 ${formatCNYPrice(
                                                  convertUSDToCNY(
                                                    route.per_call_price,
                                                    effectiveRechargeRate
                                                  )
                                                )} / 次`
                                              : convertUSDToCNY(
                                                  route.per_call_price,
                                                  faceValueRate
                                                ) !== null
                                              ? `参考 ${formatCNYPrice(
                                                  convertUSDToCNY(
                                                    route.per_call_price,
                                                    faceValueRate
                                                  )
                                                )} / 次`
                                              : '成本未校准'}
                                          </div>
                                          {perCallCostCNY === null &&
                                            perCallFaceValueCNY !== null && (
                                              <div
                                                style={{
                                                  ...helperTextStyle,
                                                  marginTop: '0.18rem',
                                                }}
                                              >
                                                成本未校准
                                              </div>
                                            )}
                                          {showPerCallReference && (
                                            <div
                                              style={{
                                                ...helperTextStyle,
                                                marginTop: '0.18rem',
                                              }}
                                            >
                                              参考{' '}
                                              {formatCNYPrice(
                                                perCallFaceValueCNY
                                              )}{' '}
                                              / 次
                                            </div>
                                          )}
                                          <div
                                            style={{
                                              ...helperTextStyle,
                                              marginTop: '0.18rem',
                                            }}
                                          >
                                            USD{' '}
                                            {formatPrice(route.per_call_price)}{' '}
                                            / 次
                                          </div>
                                          {/* The primary line already shows reference when cost is not available. */}
                                          <div
                                            style={{
                                              ...helperTextStyle,
                                              marginTop: '0.2rem',
                                            }}
                                          >
                                            分组倍率 x
                                            {Number(route.group_ratio || 1)
                                              .toFixed(4)
                                              .replace(/\.?0+$/, '')}
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ lineHeight: 1.35 }}>
                                          <div
                                            style={{
                                              color: 'var(--text-primary)',
                                              fontWeight: 600,
                                            }}
                                          >
                                            {convertUSDToCNY(
                                              promptPriceUSD,
                                              effectiveRechargeRate
                                            ) !== null &&
                                            convertUSDToCNY(
                                              route.completion_price_per_1m,
                                              effectiveRechargeRate
                                            ) !== null
                                              ? `成本 ${formatCNYPrice(
                                                  convertUSDToCNY(
                                                    promptPriceUSD,
                                                    effectiveRechargeRate
                                                  )
                                                )} / ${formatCNYPrice(
                                                  convertUSDToCNY(
                                                    route.completion_price_per_1m,
                                                    effectiveRechargeRate
                                                  )
                                                )}`
                                              : convertUSDToCNY(
                                                  promptPriceUSD,
                                                  faceValueRate
                                                ) !== null &&
                                                convertUSDToCNY(
                                                  route.completion_price_per_1m,
                                                  faceValueRate
                                                ) !== null
                                              ? `参考 ${formatCNYPrice(
                                                  convertUSDToCNY(
                                                    promptPriceUSD,
                                                    faceValueRate
                                                  )
                                                )} / ${formatCNYPrice(
                                                  convertUSDToCNY(
                                                    route.completion_price_per_1m,
                                                    faceValueRate
                                                  )
                                                )}`
                                              : '成本未校准'}
                                          </div>
                                          {!(
                                            promptCostCNY !== null &&
                                            completionCostCNY !== null
                                          ) &&
                                            promptFaceValueCNY !== null &&
                                            completionFaceValueCNY !== null && (
                                              <div
                                                style={{
                                                  ...helperTextStyle,
                                                  marginTop: '0.18rem',
                                                }}
                                              >
                                                成本未校准
                                              </div>
                                            )}
                                          {showPerTokenReference && (
                                            <div
                                              style={{
                                                ...helperTextStyle,
                                                marginTop: '0.18rem',
                                              }}
                                            >
                                              参考{' '}
                                              {formatCNYPrice(
                                                promptFaceValueCNY
                                              )}{' '}
                                              /{' '}
                                              {formatCNYPrice(
                                                completionFaceValueCNY
                                              )}
                                            </div>
                                          )}
                                          <div
                                            style={{
                                              ...helperTextStyle,
                                              marginTop: '0.18rem',
                                            }}
                                          >
                                            USD {formatPrice(promptPriceUSD)} /{' '}
                                            {formatPrice(
                                              route.completion_price_per_1m
                                            )}
                                          </div>
                                          {(Number(
                                            route.cache_read_price_per_1m
                                          ) > 0 ||
                                            Number(
                                              route.cache_create_price_per_1m
                                            ) > 0) && (
                                            <div
                                              style={{
                                                ...helperTextStyle,
                                                marginTop: '0.18rem',
                                              }}
                                            >
                                              缓存读{' '}
                                              {Number(
                                                route.cache_read_price_per_1m
                                              ) > 0
                                                ? formatPrice(
                                                    route.cache_read_price_per_1m
                                                  )
                                                : '-'}{' '}
                                              · 缓存建{' '}
                                              {Number(
                                                route.cache_create_price_per_1m
                                              ) > 0
                                                ? formatPrice(
                                                    route.cache_create_price_per_1m
                                                  )
                                                : '-'}
                                            </div>
                                          )}
                                          <div
                                            style={{
                                              ...helperTextStyle,
                                              marginTop: '0.2rem',
                                            }}
                                          >
                                            分组倍率 x
                                            {Number(route.group_ratio || 1)
                                              .toFixed(4)
                                              .replace(/\.?0+$/, '')}
                                          </div>
                                        </div>
                                      )}
                                    </Td>
                                    <Td style={cellMiddleStyle}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                        {[
                                          ['official', '官方'],
                                          ['economy', '经济'],
                                          ['mixed', '混合'],
                                        ].map(([value, label]) => {
                                          const selected = String(route.token_channel_categories || 'economy')
                                            .split(',')
                                            .map((item) => item.trim())
                                            .includes(value);
                                          return (
                                            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                                              <input
                                                type='checkbox'
                                                checked={selected}
                                                disabled={Boolean(savingTokenCategories[route.provider_token_id])}
                                                onChange={() => toggleTokenCategory(route, value)}
                                              />
                                              <span>{label}</span>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    </Td>
                                    <Td style={cellMiddleStyle}>
                                      {(() => {
                                        const isVariant = Boolean(
                                          route.channel_variant_id
                                        );
                                        const existingOverrides = isVariant
                                          ? getVariantOverrides(selectedEntry)
                                          : {};
                                        const baseRouteId =
                                          getBaseRouteId(route);
                                        const overrideVal =
                                          existingOverrides[baseRouteId]
                                            ?.priority;
                                        const draftVal =
                                          variantOverrideDrafts[baseRouteId]
                                            ?.priority;
                                        const displayPriority = isVariant
                                          ? draftVal !== undefined
                                            ? draftVal
                                            : overrideVal !== undefined
                                            ? overrideVal
                                            : route.priority
                                          : route.priority;
                                        const hasOverride =
                                          isVariant &&
                                          (draftVal !== undefined ||
                                            overrideVal !== undefined);
                                        return (
                                          <div className='routes-spinner'>
                                            <button
                                              type='button'
                                              aria-label='优先级增加'
                                              style={spinButtonStyle}
                                              onClick={() =>
                                                isVariant
                                                  ? adjustVariantOverrideNumeric(
                                                      selectedEntry,
                                                      route,
                                                      'priority',
                                                      1
                                                    )
                                                  : adjustDraftNumeric(
                                                      route,
                                                      'priority',
                                                      1
                                                    )
                                              }
                                            >
                                              ▲
                                            </button>
                                            <div
                                              className='routes-spinner-value'
                                              style={
                                                hasOverride
                                                  ? {
                                                      color:
                                                        'var(--primary-color)',
                                                      fontWeight: 600,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {displayPriority}
                                            </div>
                                            <button
                                              type='button'
                                              aria-label='优先级减少'
                                              style={spinButtonStyle}
                                              onClick={() =>
                                                isVariant
                                                  ? adjustVariantOverrideNumeric(
                                                      selectedEntry,
                                                      route,
                                                      'priority',
                                                      -1
                                                    )
                                                  : adjustDraftNumeric(
                                                      route,
                                                      'priority',
                                                      -1
                                                    )
                                              }
                                            >
                                              ▼
                                            </button>
                                          </div>
                                        );
                                      })()}
                                    </Td>
                                    <Td style={cellMiddleStyle}>
                                      {(() => {
                                        const isVariant = Boolean(
                                          route.channel_variant_id
                                        );
                                        const existingOverrides = isVariant
                                          ? getVariantOverrides(selectedEntry)
                                          : {};
                                        const baseRouteId =
                                          getBaseRouteId(route);
                                        const overrideVal =
                                          existingOverrides[baseRouteId]
                                            ?.weight;
                                        const draftVal =
                                          variantOverrideDrafts[baseRouteId]
                                            ?.weight;
                                        const displayWeight = isVariant
                                          ? draftVal !== undefined
                                            ? draftVal
                                            : overrideVal !== undefined
                                            ? overrideVal
                                            : route.weight
                                          : route.weight;
                                        const hasOverride =
                                          isVariant &&
                                          (draftVal !== undefined ||
                                            overrideVal !== undefined);
                                        return (
                                          <div className='routes-spinner'>
                                            <button
                                              type='button'
                                              aria-label='权重增加'
                                              style={spinButtonStyle}
                                              onClick={() =>
                                                isVariant
                                                  ? adjustVariantOverrideNumeric(
                                                      selectedEntry,
                                                      route,
                                                      'weight',
                                                      1
                                                    )
                                                  : adjustDraftNumeric(
                                                      route,
                                                      'weight',
                                                      1
                                                    )
                                              }
                                            >
                                              ▲
                                            </button>
                                            <div
                                              className='routes-spinner-value'
                                              style={
                                                hasOverride
                                                  ? {
                                                      color:
                                                        'var(--primary-color)',
                                                      fontWeight: 600,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {displayWeight}
                                            </div>
                                            <button
                                              type='button'
                                              aria-label='权重减少'
                                              style={spinButtonStyle}
                                              onClick={() =>
                                                isVariant
                                                  ? adjustVariantOverrideNumeric(
                                                      selectedEntry,
                                                      route,
                                                      'weight',
                                                      -1
                                                    )
                                                  : adjustDraftNumeric(
                                                      route,
                                                      'weight',
                                                      -1
                                                    )
                                              }
                                            >
                                              ▼
                                            </button>
                                          </div>
                                        );
                                      })()}
                                    </Td>
                                    <Td style={cellMiddleStyle}>
                                      {Number.isFinite(share) ? (
                                        <div className='routes-share-compact'>
                                          <div className='routes-share-track'>
                                            <div
                                              className='routes-share-fill'
                                              style={{
                                                width: `${Math.min(
                                                  100,
                                                  Math.max(0, share)
                                                )}%`,
                                              }}
                                            />
                                          </div>
                                          <div className='routes-share-meta'>
                                            <span
                                              style={{
                                                color: 'var(--text-primary)',
                                                fontWeight: 600,
                                              }}
                                            >
                                              {formatPercent(share)}
                                            </span>
                                            {shareDelta !== null &&
                                              Math.abs(shareDelta) >= 0.1 && (
                                                <span
                                                  style={{
                                                    color:
                                                      shareDelta > 0
                                                        ? 'var(--success)'
                                                        : 'var(--error)',
                                                  }}
                                                >
                                                  {shareDelta > 0 ? '+' : ''}
                                                  {shareDelta.toFixed(1)}
                                                </span>
                                              )}
                                            {isBestValue && (
                                              <span
                                                style={{
                                                  color: 'var(--success)',
                                                }}
                                              >
                                                性价比最优
                                              </span>
                                            )}
                                          </div>
                                          <div
                                            style={{
                                              ...helperTextStyle,
                                              marginTop: '0.18rem',
                                            }}
                                          >
                                            性价比（金额） | 评分{' '}
                                            {Number.isFinite(valueScore)
                                              ? valueScore.toFixed(4)
                                              : '-'}{' '}
                                            |{' '}
                                            {Number.isFinite(
                                              usageWindowHours
                                            ) && usageWindowHours > 0
                                              ? `${usageWindowHours}h`
                                              : '24h'}
                                            使用{' '}
                                            {Number.isFinite(recentUsageCost)
                                              ? formatPrice(recentUsageCost, 4)
                                              : '-'}
                                          </div>
                                          {healthEnabled && (
                                            <div
                                              style={{
                                                ...helperTextStyle,
                                                marginTop: '0.18rem',
                                                paddingTop: '0.18rem',
                                                borderTop:
                                                  '1px dashed var(--border-color)',
                                              }}
                                            >
                                              故障健康(Beta) | 健康倍率{' '}
                                              {Number.isFinite(healthMultiplier)
                                                ? `x${healthMultiplier.toFixed(
                                                    3
                                                  )}`
                                                : '-'}
                                              {hasHealthSampleThreshold && (
                                                <span>
                                                  {' '}
                                                  | 仅当样本 &gt;={' '}
                                                  {healthMinSamples} 生效
                                                </span>
                                              )}
                                              {Number.isFinite(
                                                healthFailRate
                                              ) &&
                                                Number.isFinite(
                                                  healthSuccessRate
                                                ) &&
                                                healthSampleCount > 0 && (
                                                  <span>
                                                    {' '}
                                                    | 成功率{' '}
                                                    {(
                                                      healthSuccessRate * 100
                                                    ).toFixed(1)}
                                                    % | 失败率{' '}
                                                    {(
                                                      healthFailRate * 100
                                                    ).toFixed(1)}
                                                    % | 样本 {healthSampleCount}
                                                  </span>
                                                )}
                                              {hasHealthSampleThreshold &&
                                                Number.isFinite(
                                                  healthSampleCount
                                                ) &&
                                                healthSampleCount <
                                                  healthMinSamples && (
                                                  <span
                                                    style={{
                                                      color: 'var(--warning)',
                                                    }}
                                                  >
                                                    {' '}
                                                    | 样本不足阈值，当前按
                                                    x1.000
                                                  </span>
                                                )}
                                              {showHealthEffect && (
                                                <span
                                                  style={{
                                                    color:
                                                      healthMultiplier < 1
                                                        ? 'var(--error)'
                                                        : 'var(--success)',
                                                  }}
                                                >
                                                  {' '}
                                                  |{' '}
                                                  {healthMultiplier < 1
                                                    ? '故障惩罚'
                                                    : '健康奖励'}{' '}
                                                  {healthMultiplier < 1
                                                    ? `${(
                                                        (1 - healthMultiplier) *
                                                        100
                                                      ).toFixed(1)}%`
                                                    : `+${(
                                                        (healthMultiplier - 1) *
                                                        100
                                                      ).toFixed(1)}%`}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span
                                          style={{
                                            color: 'var(--text-secondary)',
                                          }}
                                        >
                                          -
                                        </span>
                                      )}
                                    </Td>
                                    <Td style={cellMiddleStyle}>
                                      {(() => {
                                        const isVariant = Boolean(
                                          route.channel_variant_id
                                        );
                                        const routeRowKey =
                                          getRouteRowKey(route);
                                        const draftCooldownImpact =
                                          drafts[routeRowKey]
                                            ?.affects_provider_cooldown;
                                        const effectiveCooldownImpact =
                                          draftCooldownImpact !== undefined
                                            ? draftCooldownImpact
                                            : route.affects_provider_cooldown !==
                                              false;
                                        return (
                                          <select
                                            className='routes-status-select'
                                            value={
                                              effectiveCooldownImpact
                                                ? 'affects'
                                                : 'isolated'
                                            }
                                            onChange={(e) =>
                                              updateDraft(routeRowKey, {
                                                affects_provider_cooldown:
                                                  e.target.value === 'affects',
                                              })
                                            }
                                            style={statusSelectStyle}
                                            disabled={isVariant}
                                            title={
                                              isVariant
                                                ? '分裂模型请在主模型路由里修改'
                                                : undefined
                                            }
                                          >
                                            <option value='affects'>
                                              影响
                                            </option>
                                            <option value='isolated'>
                                              不影响
                                            </option>
                                          </select>
                                        );
                                      })()}
                                    </Td>
                                    <Td style={cellMiddleStyle}>
                                      {(() => {
                                        const isVariant = Boolean(
                                          route.channel_variant_id
                                        );
                                        const existingOverrides = isVariant
                                          ? getVariantOverrides(selectedEntry)
                                          : {};
                                        const baseRouteId =
                                          getBaseRouteId(route);
                                        const overrideEnabled =
                                          existingOverrides[baseRouteId]
                                            ?.enabled;
                                        const draftEnabled =
                                          variantOverrideDrafts[baseRouteId]
                                            ?.enabled;
                                        const effectiveEnabled = isVariant
                                          ? draftEnabled !== undefined
                                            ? draftEnabled
                                            : overrideEnabled !== undefined
                                            ? overrideEnabled
                                            : route.enabled
                                          : route.enabled;
                                        return (
                                          <select
                                            className='routes-status-select'
                                            value={
                                              effectiveEnabled
                                                ? 'enabled'
                                                : 'disabled'
                                            }
                                            onChange={(e) =>
                                              isVariant
                                                ? updateVariantOverride(
                                                    baseRouteId,
                                                    'enabled',
                                                    e.target.value === 'enabled'
                                                  )
                                                : updateDraft(
                                                    getRouteRowKey(route),
                                                    {
                                                      enabled:
                                                        e.target.value ===
                                                        'enabled',
                                                    }
                                                  )
                                            }
                                            style={statusSelectStyle}
                                          >
                                            <option value='enabled'>
                                              启用
                                            </option>
                                            <option value='disabled'>
                                              禁用
                                            </option>
                                          </select>
                                        );
                                      })()}
                                    </Td>
                                  </Tr>
                                );
                              })}
                            </React.Fragment>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
      <Modal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`价格历史 · ${
          selectedEntry?.modelName ||
          pricingHistory?.display_model_name ||
          selectedEntry?.groupKey ||
          '当前模型'
        }`}
        maxWidth='72rem'
        actions={
          <>
            <Button
              variant='secondary'
              onClick={() =>
                selectedEntry?.groupKey &&
                loadPricingHistory(selectedEntry.groupKey)
              }
              disabled={!selectedEntry || pricingHistoryLoading}
            >
              刷新历史
            </Button>
            <Button
              variant='primary'
              onClick={() => setHistoryModalOpen(false)}
            >
              关闭
            </Button>
          </>
        }
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '0.75rem',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <div style={{ ...helperTextStyle, maxWidth: '48rem' }}>
              折线图按当前路由权重、优先级和缓存价格规则，重算这个模型的总综合成本与总推荐售价。下面的变更列表记录的是上游进货价快照变化。
            </div>
            {pricingHistoryTrend && (
              <Badge
                color={
                  pricingHistoryTrend.direction === 'up'
                    ? 'red'
                    : pricingHistoryTrend.direction === 'down'
                    ? 'green'
                    : 'gray'
                }
              >
                {pricingHistoryTrend.direction === 'up' ? (
                  <ArrowUpRight size={14} style={{ marginRight: '0.25rem' }} />
                ) : pricingHistoryTrend.direction === 'down' ? (
                  <ArrowDownRight
                    size={14}
                    style={{ marginRight: '0.25rem' }}
                  />
                ) : (
                  <Minus size={14} style={{ marginRight: '0.25rem' }} />
                )}
                建议售价较上次 {formatSignedCNYPrice(pricingHistoryTrend.delta)}{' '}
                {formatSignedPercent(pricingHistoryTrend.percent)}
              </Badge>
            )}
          </div>
          {pricingHistoryLoading ? (
            <div style={{ ...helperTextStyle, padding: '2rem 0' }}>
              加载价格历史中...
            </div>
          ) : pricingHistoryChartData.length > 0 ? (
            <div
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)',
                padding: '0.75rem',
                backgroundColor: 'var(--bg-primary)',
              }}
            >
              <div
                style={{
                  color: 'var(--text-primary)',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                }}
              >
                总综合成本 / 总推荐售价
              </div>
              <div style={{ width: '100%', height: '320px' }}>
                <ResponsiveContainer>
                  <LineChart data={pricingHistoryChartData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='label' minTickGap={28} />
                    <YAxis
                      tickFormatter={(value) => `¥${Number(value).toFixed(2)}`}
                      width={72}
                    />
                    <Tooltip
                      formatter={(value) =>
                        Number.isFinite(Number(value))
                          ? `¥${Number(value).toFixed(4)}`
                          : '-'
                      }
                      labelFormatter={(label, payload) =>
                        payload?.[0]?.payload?.timestamp
                          ? formatHistoryTimestamp(payload[0].payload.timestamp)
                          : label
                      }
                    />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='aggregate'
                      name='总综合成本'
                      stroke='#2563eb'
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type='monotone'
                      dataKey='recommended'
                      name='总推荐售价'
                      stroke='#f97316'
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div style={{ ...helperTextStyle, padding: '2rem 0' }}>
              这个模型当前还没有可展示的上游价格历史。
            </div>
          )}
          <div
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-lg)',
              padding: '0.85rem',
            }}
          >
            <div
              style={{
                color: 'var(--text-primary)',
                fontWeight: 600,
                marginBottom: '0.5rem',
              }}
            >
              上游变更记录
            </div>
            {pricingHistoryEvents.length === 0 ? (
              <div style={helperTextStyle}>还没有检测到上游价格变更。</div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  maxHeight: '24rem',
                  overflowY: 'auto',
                }}
              >
                {pricingHistoryEvents.slice(0, 24).map((event, index) => (
                  <div
                    key={`${event.timestamp}-${event.provider_id}-${event.model_name}-${event.token_group_name}-${index}`}
                    style={{
                      paddingBottom: '0.75rem',
                      borderBottom:
                        index === Math.min(pricingHistoryEvents.length, 24) - 1
                          ? 'none'
                          : '1px dashed var(--border-color)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '0.75rem',
                        flexWrap: 'wrap',
                        marginBottom: '0.35rem',
                      }}
                    >
                      <div
                        style={{
                          color: 'var(--text-primary)',
                          fontWeight: 600,
                        }}
                      >
                        {event.provider_name} · {event.model_name}
                        {event.token_group_name
                          ? ` · 分组 ${event.token_group_name}`
                          : ''}
                      </div>
                      <div style={helperTextStyle}>
                        {formatHistoryTimestamp(event.timestamp)}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.35rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      {event.changes.map((change) => (
                        <Badge
                          key={`${event.timestamp}-${change.field}`}
                          color={change.direction === 'up' ? 'red' : 'green'}
                        >
                          {change.label} {formatSignedUSDPrice(change.delta)}{' '}
                          {change.unit}
                          {change.delta_percent !== null &&
                          change.delta_percent !== undefined
                            ? ` ${formatSignedPercent(change.delta_percent)}`
                            : ''}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
                {pricingHistoryEvents.length > 24 && (
                  <div style={helperTextStyle}>
                    仅展示最近 24 条变更，完整历史可继续向后扩展。
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModelRoutesTable;
